import { getApps, initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator, collection, doc, getDoc, query, getDocs, setDoc, serverTimestamp, updateDoc, deleteDoc, onSnapshot, writeBatch, runTransaction } from 'firebase/firestore';
export { Timestamp, limit, orderBy, query, serverTimestamp, startAfter, where } from 'firebase/firestore';
import { getAuth, connectAuthEmulator, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, OAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
import { getStorage, connectStorageEmulator, ref, uploadBytes, getDownloadURL, uploadBytesResumable, deleteObject, listAll } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { createContext, useReducer, useCallback, useEffect, useMemo, useContext, useState } from 'react';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { useLocation, Navigate } from 'react-router-dom';
import { z } from 'zod';
export { z } from 'zod';

// src/firebase/config.ts
var firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};
var app;
var db;
var auth;
var functions;
var storage;
var appCheck = null;
var emulatorsConnected = false;
function initializeFirebase() {
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
    auth = getAuth(app);
    functions = getFunctions(app);
    storage = getStorage(app);
    if (import.meta.env.PROD && import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true
      });
    }
    if (import.meta.env.VITE_USE_EMULATORS === "true" && !emulatorsConnected) {
      connectEmulators();
    }
  } else {
    app = getApps()[0];
    db = getFirestore(app);
    auth = getAuth(app);
    functions = getFunctions(app);
    storage = getStorage(app);
  }
  return app;
}
function connectEmulators() {
  if (emulatorsConnected) return;
  try {
    connectFirestoreEmulator(db, "localhost", 8080);
    connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
    connectFunctionsEmulator(functions, "localhost", 5001);
    connectStorageEmulator(storage, "localhost", 9199);
    emulatorsConnected = true;
    console.log("Connected to Firebase emulators");
  } catch (error) {
    console.warn("Failed to connect to Firebase emulators:", error);
  }
}
function isUsingEmulators() {
  return emulatorsConnected;
}
var COLLECTIONS = {
  USERS: "users",
  ORGANIZATIONS: "organizations",
  ORGBOARDS: "orgboards",
  CONSULTING_FIRMS: "consultingFirms",
  PENDING_USERS: "pending_users",
  ASSESSMENTS: "assessments",
  SESSIONS: "sessions",
  QUIZ_LEADS: "quiz_leads",
  BUDGETS: "budgets",
  BATTLE_PLANS: "battlePlans",
  AUDIT_LOGS: "audit_logs",
  SUBSCRIPTIONS: "subscriptions"
};
function getCollection(collectionName) {
  return collection(db, collectionName);
}
function getDocRef(collectionName, docId) {
  return doc(db, collectionName, docId);
}
async function getDocument(collectionName, docId) {
  const docRef = getDocRef(collectionName, docId);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) {
    return null;
  }
  return { id: snapshot.id, ...snapshot.data() };
}
async function getDocuments(collectionName, constraints = []) {
  const collectionRef = getCollection(collectionName);
  const q = query(collectionRef, ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc2) => ({ id: doc2.id, ...doc2.data() }));
}
async function createDocument(collectionName, data) {
  const collectionRef = getCollection(collectionName);
  const docRef = doc(collectionRef);
  await setDoc(docRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}
async function setDocument(collectionName, docId, data) {
  const docRef = getDocRef(collectionName, docId);
  await setDoc(docRef, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}
async function updateDocument(collectionName, docId, data) {
  const docRef = getDocRef(collectionName, docId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp()
  });
}
async function deleteDocument(collectionName, docId) {
  const docRef = doc(db, collectionName, docId);
  await deleteDoc(docRef);
}
function subscribeToDocument(collectionName, docId, callback) {
  const docRef = getDocRef(collectionName, docId);
  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({ id: snapshot.id, ...snapshot.data() });
    } else {
      callback(null);
    }
  });
}
function subscribeToCollection(collectionName, constraints, callback) {
  const collectionRef = getCollection(collectionName);
  const q = query(collectionRef, ...constraints);
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map((doc2) => ({ id: doc2.id, ...doc2.data() }));
    callback(items);
  });
}
async function batchWrite(operations) {
  const batch = writeBatch(db);
  for (const op of operations) {
    const docRef = doc(db, op.collection, op.docId);
    switch (op.type) {
      case "set":
        batch.set(docRef, { ...op.data, updatedAt: serverTimestamp() });
        break;
      case "update":
        batch.update(docRef, { ...op.data, updatedAt: serverTimestamp() });
        break;
      case "delete":
        batch.delete(docRef);
        break;
    }
  }
  await batch.commit();
}
async function executeTransaction(transactionFn) {
  return runTransaction(db, transactionFn);
}
async function callFunction(functionName, data) {
  const callable = httpsCallable(functions, functionName);
  const result = await callable(data);
  return result.data;
}
var cloudFunctions = {
  // Assessment functions
  assessment: {
    initialize: () => callFunction("initializeAssessments", void 0),
    getByType: (type, language) => callFunction("getAssessmentsByType", { type, language }),
    get: (id) => callFunction("getAssessment", { id })
  },
  // Session functions
  session: {
    create: (data) => callFunction("createSession", data),
    get: (sessionId) => callFunction("getSessionById", { id: sessionId }),
    submitResponses: (data) => callFunction("submitResponses", data)
  },
  // Admin functions
  admin: {
    getCompletedSessions: (filters) => callFunction("getCompletedSessions", filters || {}),
    searchSessions: (query2) => callFunction("searchSessions", { query: query2 }),
    exportToCSV: (sessionIds) => callFunction("exportSessionsToCSV", { sessionIds }),
    updateParticipant: (sessionId, data) => callFunction("updateSessionParticipant", {
      sessionId,
      data
    }),
    deleteSessions: (sessionIds) => callFunction("deleteSessions", { sessionIds })
  },
  // User management functions
  users: {
    list: () => callFunction("adminUsers", void 0),
    create: (data) => callFunction("adminCreateUser", data),
    update: (uid, data) => callFunction("adminUpdateUser", { uid, data }),
    delete: (uid) => callFunction("adminDeleteUser", { uid }),
    setRole: (uid, role) => callFunction("adminSetUserRole", { uid, role })
  },
  // Organization functions
  organization: {
    upsert: (data) => callFunction("upsertOrganization", data),
    list: () => callFunction("listOrganizationsWithStats", void 0),
    generateReport: (orgId) => callFunction("generateOrganizationReport", { orgId })
  },
  // Consulting firm functions
  firm: {
    create: (data) => callFunction("createConsultingFirm", data),
    update: (firmId, data) => callFunction("updateConsultingFirm", {
      firmId,
      data
    }),
    list: () => callFunction("listConsultingFirms", void 0),
    delete: (firmId) => callFunction("deleteConsultingFirm", { firmId }),
    addConsultant: (firmId, userId) => callFunction("addConsultantToFirm", { firmId, userId }),
    removeConsultant: (firmId, userId) => callFunction("removeConsultantFromFirm", { firmId, userId })
  },
  // Invite functions
  invite: {
    create: (data) => callFunction("createInvite", data),
    validate: (code) => callFunction("validateInvite", { code }),
    list: (organizationId) => callFunction("listInvites", { organizationId }),
    expire: (inviteId) => callFunction("expireInvite", { inviteId })
  },
  // Chart functions
  chart: {
    generate: (sessionId) => callFunction("generateChart", { sessionId }),
    compare: (sessionIds) => callFunction("generateComparisonChart", { sessionIds })
  },
  // Notification functions
  notification: {
    sendAssessment: (sessionId) => callFunction("sendAssessmentNotification", { sessionId })
  },
  // Quiz lead functions
  quizLead: {
    submit: (data) => callFunction("submitQuizLead", data),
    list: () => callFunction("getQuizLeads", void 0),
    updateStatus: (leadId, status) => callFunction("updateQuizLeadStatus", { leadId, status })
  },
  // Billing functions
  billing: {
    createCheckout: (plan, organizationId) => callFunction(
      "createCheckoutSession",
      { plan, organizationId }
    ),
    createPortal: (organizationId) => callFunction(
      "createPortalSession",
      { organizationId }
    ),
    getStatus: (organizationId) => callFunction(
      "getSubscriptionStatus",
      { organizationId }
    )
  }
};
async function uploadFile(path, file, metadata) {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, metadata);
  return getDownloadURL(storageRef);
}
function uploadFileWithProgress(path, file, onProgress, metadata) {
  const storageRef = ref(storage, path);
  const task = uploadBytesResumable(storageRef, file, metadata);
  const promise = new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => {
        const progress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
        onProgress?.(progress);
      },
      (error) => {
        reject(error);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve(url);
      }
    );
  });
  return { task, promise };
}
async function getFileUrl(path) {
  const storageRef = ref(storage, path);
  return getDownloadURL(storageRef);
}
async function deleteFile(path) {
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
}
async function listFiles(path) {
  const storageRef = ref(storage, path);
  const result = await listAll(storageRef);
  return result.items.map((item) => ({
    name: item.name,
    fullPath: item.fullPath
  }));
}
function generateFilePath(folder, fileName, userId) {
  const timestamp = Date.now();
  const sanitizedName = fileName.replace(/[^a-zA-Z0-9.-]/g, "_");
  const userPrefix = userId ? `${userId}/` : "";
  return `${folder}/${userPrefix}${timestamp}_${sanitizedName}`;
}
var STORAGE_PATHS = {
  USER_AVATARS: "avatars",
  ORG_LOGOS: "org-logos",
  FIRM_LOGOS: "firm-logos",
  EXPORTS: "exports",
  CHARTS: "charts",
  ATTACHMENTS: "attachments"
};

// src/types/user.ts
var ROLES = {
  ADMIN: "admin",
  ORG_ADMIN: "org_admin",
  ORG_USER: "org_user",
  FIRM_LEAD: "firm_lead",
  CONSULTANT: "consultant",
  VIEWER: "viewer"
};
var ROLE_HIERARCHY = {
  admin: 100,
  firm_lead: 80,
  org_admin: 60,
  consultant: 40,
  org_user: 20,
  viewer: 10
};
var initialState = {
  user: null,
  userProfile: null,
  isLoading: true,
  isAuthenticated: false,
  error: null
};
function authReducer(state, action) {
  switch (action.type) {
    case "AUTH_STATE_CHANGED":
      return {
        ...state,
        user: action.user,
        isAuthenticated: !!action.user,
        isLoading: action.user ? state.isLoading : false,
        error: null
      };
    case "USER_PROFILE_LOADED":
      return {
        ...state,
        userProfile: action.profile,
        isLoading: false
      };
    case "AUTH_ERROR":
      return {
        ...state,
        error: action.error,
        isLoading: false
      };
    case "AUTH_LOADING":
      return {
        ...state,
        isLoading: action.isLoading
      };
    case "SIGN_OUT":
      return {
        ...initialState,
        isLoading: false
      };
    default:
      return state;
  }
}
var AuthContext = createContext(null);
function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const loadUserProfile = useCallback(async (firebaseUser) => {
    try {
      let profile = await getDocument(COLLECTIONS.USERS, firebaseUser.uid);
      if (!profile) {
        const newProfile = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || "",
          displayName: firebaseUser.displayName || firebaseUser.email?.split("@")[0] || "User",
          photoURL: firebaseUser.photoURL || void 0,
          role: ROLES.VIEWER,
          isActive: true
        };
        await setDocument(COLLECTIONS.USERS, firebaseUser.uid, newProfile);
        profile = { id: firebaseUser.uid, ...newProfile };
      }
      dispatch({ type: "USER_PROFILE_LOADED", profile });
    } catch (error) {
      console.error("Error loading user profile:", error);
      dispatch({ type: "AUTH_ERROR", error });
    }
  }, []);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      dispatch({ type: "AUTH_STATE_CHANGED", user: firebaseUser });
      if (firebaseUser) {
        await loadUserProfile(firebaseUser);
      }
    });
    return () => unsubscribe();
  }, [loadUserProfile]);
  const signInWithGoogle = useCallback(async () => {
    try {
      dispatch({ type: "AUTH_LOADING", isLoading: true });
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      dispatch({ type: "AUTH_ERROR", error });
      throw error;
    }
  }, []);
  const signInWithMicrosoft = useCallback(async () => {
    try {
      dispatch({ type: "AUTH_LOADING", isLoading: true });
      const provider = new OAuthProvider("microsoft.com");
      provider.setCustomParameters({
        prompt: "select_account"
      });
      await signInWithPopup(auth, provider);
    } catch (error) {
      dispatch({ type: "AUTH_ERROR", error });
      throw error;
    }
  }, []);
  const signInWithEmail = useCallback(async (email, password) => {
    try {
      dispatch({ type: "AUTH_LOADING", isLoading: true });
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      dispatch({ type: "AUTH_ERROR", error });
      throw error;
    }
  }, []);
  const signUp = useCallback(async (email, password, displayName) => {
    try {
      dispatch({ type: "AUTH_LOADING", isLoading: true });
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(user, { displayName });
      const newProfile = {
        uid: user.uid,
        email,
        displayName,
        role: ROLES.VIEWER,
        isActive: true
      };
      await setDocument(COLLECTIONS.USERS, user.uid, newProfile);
    } catch (error) {
      dispatch({ type: "AUTH_ERROR", error });
      throw error;
    }
  }, []);
  const signOut$1 = useCallback(async () => {
    try {
      await signOut(auth);
      dispatch({ type: "SIGN_OUT" });
    } catch (error) {
      dispatch({ type: "AUTH_ERROR", error });
      throw error;
    }
  }, []);
  const resetPassword = useCallback(async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      dispatch({ type: "AUTH_ERROR", error });
      throw error;
    }
  }, []);
  const updateProfile$1 = useCallback(
    async (data) => {
      if (!state.user) throw new Error("No user logged in");
      try {
        await updateProfile(state.user, data);
        await updateDocument(COLLECTIONS.USERS, state.user.uid, data);
        await loadUserProfile(state.user);
      } catch (error) {
        dispatch({ type: "AUTH_ERROR", error });
        throw error;
      }
    },
    [state.user, loadUserProfile]
  );
  const hasRole = useCallback(
    (role) => {
      return state.userProfile?.role === role;
    },
    [state.userProfile]
  );
  const hasAnyRole = useCallback(
    (roles) => {
      return state.userProfile?.role ? roles.includes(state.userProfile.role) : false;
    },
    [state.userProfile]
  );
  const hasMinRole = useCallback(
    (minRole) => {
      if (!state.userProfile?.role) return false;
      return ROLE_HIERARCHY[state.userProfile.role] >= ROLE_HIERARCHY[minRole];
    },
    [state.userProfile]
  );
  const value = useMemo(
    () => ({
      ...state,
      signInWithGoogle,
      signInWithMicrosoft,
      signInWithEmail,
      signUp,
      signOut: signOut$1,
      resetPassword,
      updateProfile: updateProfile$1,
      hasRole,
      hasAnyRole,
      hasMinRole,
      isAdmin: state.userProfile?.role === ROLES.ADMIN,
      isOrgAdmin: hasAnyRole([ROLES.ADMIN, ROLES.ORG_ADMIN])
    }),
    [state, signInWithGoogle, signInWithMicrosoft, signInWithEmail, signUp, signOut$1, resetPassword, updateProfile$1, hasRole, hasAnyRole, hasMinRole]
  );
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
function useUser() {
  const { userProfile, isLoading, user: firebaseUser } = useAuth();
  const updateUser = useCallback(
    async (data) => {
      if (!firebaseUser) throw new Error("No user logged in");
      await updateDocument(COLLECTIONS.USERS, firebaseUser.uid, data);
    },
    [firebaseUser]
  );
  const updatePreferences = useCallback(
    async (preferences) => {
      if (!firebaseUser || !userProfile) throw new Error("No user logged in");
      const updatedPreferences = {
        locale: preferences.locale ?? userProfile.preferences?.locale ?? "he",
        theme: preferences.theme ?? userProfile.preferences?.theme ?? "system",
        notifications: preferences.notifications ?? userProfile.preferences?.notifications ?? true
      };
      await updateDocument(COLLECTIONS.USERS, firebaseUser.uid, {
        preferences: updatedPreferences
      });
    },
    [firebaseUser, userProfile]
  );
  return {
    user: userProfile,
    isLoading,
    updateUser,
    updatePreferences
  };
}
function useRoles() {
  const { userProfile } = useAuth();
  const role = userProfile?.role ?? null;
  const hasRole = useCallback(
    (checkRole) => {
      return role === checkRole;
    },
    [role]
  );
  const hasAnyRole = useCallback(
    (roles) => {
      return role ? roles.includes(role) : false;
    },
    [role]
  );
  const hasMinRole = useCallback(
    (minRole) => {
      if (!role) return false;
      return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[minRole];
    },
    [role]
  );
  const isAdmin = role === ROLES.ADMIN;
  const isOrgAdmin = hasAnyRole([ROLES.ADMIN, ROLES.ORG_ADMIN]);
  const isFirmLead = hasAnyRole([ROLES.ADMIN, ROLES.FIRM_LEAD]);
  const isConsultant = hasAnyRole([ROLES.ADMIN, ROLES.FIRM_LEAD, ROLES.CONSULTANT]);
  const canManageOrg = hasMinRole(ROLES.ORG_ADMIN);
  const canManageFirm = hasMinRole(ROLES.FIRM_LEAD);
  const canViewReports = hasMinRole(ROLES.VIEWER);
  const canEditContent = hasMinRole(ROLES.ORG_USER);
  return useMemo(
    () => ({
      role,
      hasRole,
      hasAnyRole,
      hasMinRole,
      isAdmin,
      isOrgAdmin,
      isFirmLead,
      isConsultant,
      canManageOrg,
      canManageFirm,
      canViewReports,
      canEditContent
    }),
    [role, hasRole, hasAnyRole, hasMinRole, isAdmin, isOrgAdmin, isFirmLead, isConsultant, canManageOrg, canManageFirm, canViewReports, canEditContent]
  );
}
function RequireAuth({ children, redirectTo = "/login" }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return null;
  }
  if (!isAuthenticated) {
    return /* @__PURE__ */ jsx(Navigate, { to: redirectTo, state: { from: location }, replace: true });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function RequireRole({
  children,
  roles,
  redirectTo = "/unauthorized",
  fallback
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasAnyRole } = useRoles();
  const location = useLocation();
  if (isLoading) {
    return null;
  }
  if (!isAuthenticated) {
    return /* @__PURE__ */ jsx(Navigate, { to: "/login", state: { from: location }, replace: true });
  }
  if (!hasAnyRole(roles)) {
    if (fallback) {
      return /* @__PURE__ */ jsx(Fragment, { children: fallback });
    }
    return /* @__PURE__ */ jsx(Navigate, { to: redirectTo, state: { from: location }, replace: true });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function RequireMinRole({
  children,
  minRole,
  redirectTo = "/unauthorized",
  fallback
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasMinRole } = useRoles();
  const location = useLocation();
  if (isLoading) {
    return null;
  }
  if (!isAuthenticated) {
    return /* @__PURE__ */ jsx(Navigate, { to: "/login", state: { from: location }, replace: true });
  }
  if (!hasMinRole(minRole)) {
    if (fallback) {
      return /* @__PURE__ */ jsx(Fragment, { children: fallback });
    }
    return /* @__PURE__ */ jsx(Navigate, { to: redirectTo, state: { from: location }, replace: true });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function RequireOrg({
  children,
  orgId,
  redirectTo = "/unauthorized",
  fallback
}) {
  const { isAuthenticated, isLoading, userProfile } = useAuth();
  const { isAdmin } = useRoles();
  const location = useLocation();
  if (isLoading) {
    return null;
  }
  if (!isAuthenticated) {
    return /* @__PURE__ */ jsx(Navigate, { to: "/login", state: { from: location }, replace: true });
  }
  if (isAdmin) {
    return /* @__PURE__ */ jsx(Fragment, { children });
  }
  const hasAccess = userProfile?.organizationId === orgId || userProfile?.assignedClientOrgIds?.includes(orgId);
  if (!hasAccess) {
    if (fallback) {
      return /* @__PURE__ */ jsx(Fragment, { children: fallback });
    }
    return /* @__PURE__ */ jsx(Navigate, { to: redirectTo, state: { from: location }, replace: true });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function ConditionalRender({
  children,
  roles,
  minRole,
  fallback = null
}) {
  const { hasAnyRole, hasMinRole } = useRoles();
  if (roles && !hasAnyRole(roles)) {
    return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  }
  if (minRole && !hasMinRole(minRole)) {
    return /* @__PURE__ */ jsx(Fragment, { children: fallback });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function useFirestoreDoc(collection2, docId, options = { subscribe: true }) {
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null
  });
  const fetchDocument = useCallback(async () => {
    if (!docId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const doc2 = await getDocument(collection2, docId);
      setState({ data: doc2, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error });
    }
  }, [collection2, docId]);
  useEffect(() => {
    if (!docId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    if (options.subscribe) {
      setState((prev) => ({ ...prev, loading: true }));
      const unsubscribe = subscribeToDocument(collection2, docId, (data) => {
        setState({ data, loading: false, error: null });
      });
      return () => unsubscribe();
    } else {
      fetchDocument();
    }
  }, [collection2, docId, options.subscribe, fetchDocument]);
  const refresh = useCallback(async () => {
    await fetchDocument();
  }, [fetchDocument]);
  const update = useCallback(
    async (data) => {
      if (!docId) throw new Error("No document ID provided");
      await updateDocument(collection2, docId, data);
    },
    [collection2, docId]
  );
  return {
    ...state,
    refresh,
    update
  };
}
function useFirestoreCollection(collection2, options = { subscribe: true, constraints: [] }) {
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: null
  });
  const constraintsKey = useMemo(
    () => JSON.stringify(options.constraints?.map((c) => c.toString()) || []),
    [options.constraints]
  );
  const fetchDocuments = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const docs = await getDocuments(collection2, options.constraints);
      setState({ data: docs, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error });
    }
  }, [collection2, constraintsKey]);
  useEffect(() => {
    if (options.subscribe) {
      setState((prev) => ({ ...prev, loading: true }));
      const unsubscribe = subscribeToCollection(
        collection2,
        options.constraints || [],
        (data) => {
          setState({ data, loading: false, error: null });
        }
      );
      return () => unsubscribe();
    } else {
      fetchDocuments();
    }
  }, [collection2, options.subscribe, constraintsKey, fetchDocuments]);
  const refresh = useCallback(async () => {
    await fetchDocuments();
  }, [fetchDocuments]);
  return {
    ...state,
    refresh,
    isEmpty: !state.loading && (!state.data || state.data.length === 0),
    count: state.data?.length || 0
  };
}
function useCloudFunction(functionName) {
  const [state, setState] = useState({
    data: null,
    loading: false,
    error: null
  });
  const execute = useCallback(
    async (data) => {
      setState({ data: null, loading: true, error: null });
      try {
        const result = await callFunction(functionName, data);
        setState({ data: result, loading: false, error: null });
        return result;
      } catch (error) {
        setState({ data: null, loading: false, error });
        throw error;
      }
    },
    [functionName]
  );
  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);
  return {
    ...state,
    execute,
    reset
  };
}
function useCloudFunctionQuery(functionName, data, options = { enabled: true }) {
  const [state, setState] = useState({
    data: null,
    loading: options.enabled ?? true,
    error: null
  });
  const fetch = useCallback(async () => {
    if (!options.enabled) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await callFunction(functionName, data);
      setState({ data: result, loading: false, error: null });
    } catch (error) {
      setState({ data: null, loading: false, error });
    }
  }, [functionName, data, options.enabled]);
  useState(() => {
    if (options.enabled) {
      fetch();
    }
  });
  return {
    ...state,
    refetch: fetch
  };
}
function useOrganization() {
  const { userProfile, isLoading: authLoading } = useAuth();
  const organizationId = userProfile?.organizationId ?? null;
  const {
    data: organization,
    loading: orgLoading,
    error: orgError
  } = useFirestoreDoc(COLLECTIONS.ORGANIZATIONS, organizationId || void 0);
  const {
    data: orgBoard,
    loading: boardLoading,
    error: boardError
  } = useFirestoreDoc(COLLECTIONS.ORGBOARDS, organizationId || void 0);
  const isLoading = authLoading || orgLoading || boardLoading;
  const error = orgError || boardError;
  return useMemo(
    () => ({
      organization,
      orgBoard,
      organizationId,
      isLoading,
      error,
      hasOrganization: !!organization
    }),
    [organization, orgBoard, organizationId, isLoading, error]
  );
}
function useOrganizationById(orgId) {
  const {
    data: organization,
    loading: orgLoading,
    error: orgError
  } = useFirestoreDoc(COLLECTIONS.ORGANIZATIONS, orgId);
  const {
    data: orgBoard,
    loading: boardLoading,
    error: boardError
  } = useFirestoreDoc(COLLECTIONS.ORGBOARDS, orgId);
  return useMemo(
    () => ({
      organization,
      orgBoard,
      isLoading: orgLoading || boardLoading,
      error: orgError || boardError
    }),
    [organization, orgBoard, orgLoading, boardLoading, orgError, boardError]
  );
}

// src/types/subscription.ts
var PLAN_TIERS = {
  STARTER: "starter",
  PROFESSIONAL: "professional",
  BUSINESS: "business",
  ENTERPRISE: "enterprise",
  ENTERPRISE_PLUS: "enterprise_plus"
};
var SUBSCRIPTION_STATUSES = {
  TRIALING: "trialing",
  ACTIVE: "active",
  PAST_DUE: "past_due",
  PAUSED: "paused",
  CANCELLED: "cancelled",
  EXPIRED: "expired"
};
var PLAN_LIMITS = {
  starter: { maxUsers: 5, maxCompanies: 3, priceMonthly: 99, priceYearly: 950 },
  professional: { maxUsers: 10, maxCompanies: 10, priceMonthly: 129, priceYearly: 1238 },
  business: { maxUsers: 20, maxCompanies: 25, priceMonthly: 178, priceYearly: 1710 },
  enterprise: { maxUsers: 50, maxCompanies: -1, priceMonthly: 297, priceYearly: 2851 },
  enterprise_plus: { maxUsers: 100, maxCompanies: -1, priceMonthly: 394, priceYearly: 3782 }
};
var PLAN_DISPLAY_NAMES = {
  starter: "Starter",
  professional: "Professional",
  business: "Business",
  enterprise: "Enterprise",
  enterprise_plus: "Enterprise Plus"
};
var TRIAL_DURATION_DAYS = 30;

// src/hooks/useSubscription.ts
function useSubscription(organizationId) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!organizationId) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = subscribeToDocument(
      COLLECTIONS.SUBSCRIPTIONS,
      organizationId,
      (data) => {
        setSubscription(data);
        setLoading(false);
        setError(null);
      }
    );
    return () => unsubscribe();
  }, [organizationId]);
  const computed = useMemo(() => {
    if (!subscription) {
      return {
        isTrialing: false,
        isActive: false,
        isPastDue: false,
        isCancelled: false,
        isExpired: false,
        trialDaysLeft: 0,
        canAccess: false,
        planLimits: PLAN_LIMITS.starter
      };
    }
    const now = /* @__PURE__ */ new Date();
    const trialEnd = subscription.trialEndDate instanceof Date ? subscription.trialEndDate : new Date(subscription.trialEndDate);
    const trialDaysLeft = Math.max(
      0,
      Math.ceil((trialEnd.getTime() - now.getTime()) / (1e3 * 60 * 60 * 24))
    );
    const isTrialing = subscription.status === "trialing" && trialDaysLeft > 0;
    const isActive = subscription.status === "active";
    const isPastDue = subscription.status === "past_due";
    const isCancelled = subscription.status === "cancelled";
    const isExpired = subscription.status === "expired" || subscription.status === "trialing" && trialDaysLeft <= 0;
    const canAccess = isActive || isTrialing;
    const planLimits = PLAN_LIMITS[subscription.plan] || PLAN_LIMITS.starter;
    return {
      isTrialing,
      isActive,
      isPastDue,
      isCancelled,
      isExpired,
      trialDaysLeft,
      canAccess,
      planLimits
    };
  }, [subscription]);
  return {
    subscription,
    loading,
    error,
    ...computed
  };
}
function useOrganizationId() {
  const [organizationId, setOrganizationId] = useState(void 0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setOrganizationId(void 0);
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      try {
        const userDoc = await getDocument(
          COLLECTIONS.USERS,
          user.uid
        );
        setOrganizationId(userDoc?.organizationId || void 0);
        setIsAdmin(userDoc?.role === "admin");
      } catch (error) {
        console.error("Failed to fetch user organizationId:", error);
        setOrganizationId(void 0);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);
  return { organizationId, isAdmin, loading };
}
function useMediaQuery(query2) {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia(query2).matches;
    }
    return false;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia(query2);
    const handler = (event) => setMatches(event.matches);
    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [query2]);
  return matches;
}
function useIsMobile() {
  return useMediaQuery("(max-width: 599px)");
}
function useIsTablet() {
  return useMediaQuery("(min-width: 600px) and (max-width: 899px)");
}
function useIsDesktop() {
  return useMediaQuery("(min-width: 900px)");
}
function useIsLargeDesktop() {
  return useMediaQuery("(min-width: 1200px)");
}
function usePrefersDarkMode() {
  return useMediaQuery("(prefers-color-scheme: dark)");
}
function usePrefersReducedMotion() {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}

// src/types/assessment.ts
var SESSION_STATUS = {
  STARTED: "started",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  ABANDONED: "abandoned"
};

// src/types/budget.ts
var INTERVAL_TYPES = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  YEARLY: "yearly"
};
var ENTRY_TYPES = {
  CREDIT: "credit",
  DEBIT: "debit",
  CALCULATED: "calculated",
  ACCUMULATIVE: "accumulative"
};

// src/types/planning.ts
var ACTION_STATUS = {
  TODO: "todo",
  IN_PROGRESS: "in_progress",
  DONE: "done",
  BLOCKED: "blocked",
  CANCELLED: "cancelled"
};
var PRIORITIES = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  URGENT: "urgent"
};

// src/utils/formatters.ts
function formatDate(date, locale = "en", options) {
  const d = new Date(date);
  const defaultOptions = {
    year: "numeric",
    month: "short",
    day: "numeric"
  };
  return d.toLocaleDateString(locale === "he" ? "he-IL" : "en-US", options || defaultOptions);
}
function formatTime(date, locale = "en", options) {
  const d = new Date(date);
  const defaultOptions = {
    hour: "2-digit",
    minute: "2-digit"
  };
  return d.toLocaleTimeString(locale === "he" ? "he-IL" : "en-US", options || defaultOptions);
}
function formatDateTime(date, locale = "en", options) {
  const d = new Date(date);
  const defaultOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  };
  return d.toLocaleString(locale === "he" ? "he-IL" : "en-US", options || defaultOptions);
}
function formatRelativeTime(date, locale = "en") {
  const d = new Date(date);
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1e3);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const rtf = new Intl.RelativeTimeFormat(locale === "he" ? "he-IL" : "en-US", {
    numeric: "auto"
  });
  if (diffDay > 0) return rtf.format(-diffDay, "day");
  if (diffHour > 0) return rtf.format(-diffHour, "hour");
  if (diffMin > 0) return rtf.format(-diffMin, "minute");
  return rtf.format(-diffSec, "second");
}
function formatNumber(value, locale = "en", options) {
  return value.toLocaleString(locale === "he" ? "he-IL" : "en-US", options);
}
function formatCurrency(value, currency = "USD", locale = "en") {
  return value.toLocaleString(locale === "he" ? "he-IL" : "en-US", {
    style: "currency",
    currency: locale === "he" ? "ILS" : currency
  });
}
function formatPercent(value, decimals = 0, locale = "en") {
  return value.toLocaleString(locale === "he" ? "he-IL" : "en-US", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}
function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + " " + sizes[i];
}
function formatPhone(phone) {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("972")) {
    const local = cleaned.slice(3);
    if (local.length === 9) {
      return `+972 ${local.slice(0, 2)}-${local.slice(2, 5)}-${local.slice(5)}`;
    }
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
function toTitleCase(text) {
  return text.toLowerCase().split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}
var emailSchema = z.string().email("Invalid email address");
var passwordSchema = z.string().min(8, "Password must be at least 8 characters").regex(/[a-zA-Z]/, "Password must contain at least one letter").regex(/[0-9]/, "Password must contain at least one number");
var phoneSchema = z.string().regex(/^[\d\s\-+()]+$/, "Invalid phone number").min(7, "Phone number too short").optional();
var urlSchema = z.string().url("Invalid URL").optional();
var requiredString = z.string().min(1, "This field is required");
var displayNameSchema = z.string().min(2, "Name must be at least 2 characters").max(50, "Name must be at most 50 characters");
var organizationNameSchema = z.string().min(2, "Organization name must be at least 2 characters").max(100, "Organization name must be at most 100 characters");
function isValidEmail(email) {
  return emailSchema.safeParse(email).success;
}
function isValidPassword(password) {
  const result = passwordSchema.safeParse(password);
  if (result.success) {
    return { valid: true, errors: [] };
  }
  return {
    valid: false,
    errors: result.error.errors.map((e) => e.message)
  };
}
function isValidPhone(phone) {
  if (!phone) return true;
  return phoneSchema.safeParse(phone).success;
}
function isValidUrl(url) {
  if (!url) return true;
  return urlSchema.safeParse(url).success;
}
function createValidator(schema) {
  return {
    validate: (data) => {
      const result = schema.safeParse(data);
      if (result.success) {
        return { valid: true, data: result.data };
      }
      return {
        valid: false,
        errors: result.error.errors.map((e) => `${e.path.join(".")}: ${e.message}`)
      };
    },
    isValid: (data) => schema.safeParse(data).success
  };
}

// src/utils/constants.ts
var APP_NAME = "Business Platform";
var APP_VERSION = "0.1.0";
var SUPPORTED_LOCALES = ["en", "he"];
var DEFAULT_LOCALE = "en";
var RTL_LOCALES = ["he"];
var DATE_FORMATS = {
  short: "MM/dd/yyyy",
  long: "MMMM d, yyyy",
  iso: "yyyy-MM-dd"
};
var DEFAULT_PAGE_SIZE = 25;
var PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
var STORAGE_KEYS = {
  AUTH_TOKEN: "auth_token",
  USER_PREFERENCES: "user_preferences",
  LOCALE: "locale",
  THEME: "theme",
  SIDEBAR_COLLAPSED: "sidebar_collapsed"
};
var ERROR_CODES = {
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR"
};
var ASSESSMENT_TYPES = {
  ASSESSMENT_360: "360",
  PPA: "PPA",
  EPOC: "EPOC",
  LB1: "LB1"
};
var CHART_COLORS = {
  primary: "#1976d2",
  secondary: "#9c27b0",
  success: "#2e7d32",
  warning: "#ed6c02",
  error: "#d32f2f",
  info: "#0288d1",
  series: [
    "#1976d2",
    "#9c27b0",
    "#2e7d32",
    "#ed6c02",
    "#d32f2f",
    "#0288d1",
    "#7b1fa2",
    "#00796b",
    "#f57c00",
    "#c2185b"
  ]
};
var STATUS_COLORS = {
  active: "#2e7d32",
  inactive: "#757575",
  pending: "#ed6c02",
  error: "#d32f2f"
};
var ANIMATION_DURATIONS = {
  fast: 150,
  normal: 300,
  slow: 500
};
var DEBOUNCE_DELAYS = {
  search: 300,
  input: 150,
  resize: 100
};

// src/utils/storage.ts
function getStorageItem(key, defaultValue) {
  if (typeof window === "undefined") return defaultValue;
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item);
  } catch {
    return defaultValue;
  }
}
function setStorageItem(key, value) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }
}
function removeStorageItem(key) {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error removing from localStorage:", error);
  }
}
function clearStorage() {
  if (typeof window === "undefined") return;
  try {
    localStorage.clear();
  } catch (error) {
    console.error("Error clearing localStorage:", error);
  }
}
function getSessionItem(key, defaultValue) {
  if (typeof window === "undefined") return defaultValue;
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item);
  } catch {
    return defaultValue;
  }
}
function setSessionItem(key, value) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Error saving to sessionStorage:", error);
  }
}
function removeSessionItem(key) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error("Error removing from sessionStorage:", error);
  }
}
function clearSession() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error("Error clearing sessionStorage:", error);
  }
}
function createNamespacedStorage(namespace) {
  const prefix = `${namespace}:`;
  return {
    get: (key, defaultValue) => getStorageItem(`${prefix}${key}`, defaultValue),
    set: (key, value) => setStorageItem(`${prefix}${key}`, value),
    remove: (key) => removeStorageItem(`${prefix}${key}`),
    clear: () => {
      if (typeof window === "undefined") return;
      Object.keys(localStorage).filter((key) => key.startsWith(prefix)).forEach((key) => localStorage.removeItem(key));
    }
  };
}

// src/i18n/dictionaries/en.json
var en_default = {
  general: {
    loading: "Loading...",
    saving: "Saving...",
    error: "An error occurred",
    success: "Success",
    comingSoon: "Coming Soon"
  },
  login: {
    title: "Sign In",
    subtitle: "Sign in to your account",
    email: "Email",
    emailPlaceholder: "Enter your email",
    password: "Password",
    passwordPlaceholder: "Enter your password",
    signIn: "Sign In",
    or: "or",
    continueWithGoogle: "Continue with Google",
    continueWithMicrosoft: "Continue with Microsoft",
    forgotPassword: "Forgot password?",
    needHelp: "Need help?",
    contactSupport: "Contact Support",
    noAccount: "Don't have an account?",
    register: "Register"
  },
  common: {
    add: "Add",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save",
    update: "Update",
    submit: "Submit",
    search: "Search",
    filter: "Filter",
    reset: "Reset",
    clear: "Clear",
    close: "Close",
    back: "Back",
    next: "Next",
    previous: "Previous",
    loading: "Loading...",
    saving: "Saving...",
    noData: "No data available",
    error: "An error occurred",
    success: "Success",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    all: "All",
    none: "None",
    select: "Select",
    required: "Required",
    optional: "Optional",
    actions: "Actions",
    details: "Details",
    settings: "Settings",
    help: "Help",
    logout: "Logout",
    login: "Login",
    signUp: "Sign Up",
    welcome: "Welcome",
    home: "Home",
    dashboard: "Dashboard",
    profile: "Profile",
    notifications: "Notifications",
    export: "Export",
    import: "Import",
    download: "Download",
    upload: "Upload",
    print: "Print",
    share: "Share",
    showing: "Showing",
    of: "of",
    perPage: "per page",
    clearFilters: "Clear Filters",
    confirmDelete: "Are you sure you want to delete this?",
    total: "Total",
    active: "Active",
    addNew: "Add New",
    contactPerson: "Contact Person",
    email: "Email",
    name: "Name",
    owner: "Owner",
    phone: "Phone",
    size: "Size",
    status: "Status",
    description: "Description",
    title: "Title",
    company: "Company",
    address: "Address",
    website: "Website"
  },
  auth: {
    signIn: "Sign In",
    signOut: "Sign Out",
    signUp: "Sign Up",
    signInWithGoogle: "Sign in with Google",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    forgotPassword: "Forgot Password?",
    resetPassword: "Reset Password",
    rememberMe: "Remember me",
    newUser: "New user?",
    alreadyHaveAccount: "Already have an account?",
    createAccount: "Create Account",
    invalidCredentials: "Invalid email or password",
    emailRequired: "Email is required",
    passwordRequired: "Password is required",
    passwordMismatch: "Passwords do not match"
  },
  navigation: {
    assessments: "Assessments",
    admin: "Admin",
    orgBoard: "Org Board",
    budget: "Budget",
    planning: "Planning",
    reports: "Reports",
    users: "Users",
    organizations: "Organizations",
    settings: "Settings"
  },
  user: {
    name: "Name",
    email: "Email",
    role: "Role",
    organization: "Organization",
    phone: "Phone",
    position: "Position",
    department: "Department",
    lastLogin: "Last Login",
    createdAt: "Created",
    status: "Status",
    active: "Active",
    inactive: "Inactive"
  },
  roles: {
    admin: "Administrator",
    org_admin: "Organization Admin",
    org_user: "Organization User",
    firm_lead: "Firm Lead",
    consultant: "Consultant",
    viewer: "Viewer"
  },
  organization: {
    name: "Organization Name",
    divisions: "Divisions",
    departments: "Departments",
    employees: "Employees",
    createOrg: "Create Organization",
    editOrg: "Edit Organization",
    orgBoard: "Organization Board",
    noOrganization: "No organization assigned"
  },
  assessment: {
    title: "Assessment",
    type: "Type",
    start: "Start Assessment",
    continue: "Continue",
    complete: "Complete",
    results: "Results",
    score: "Score",
    category: "Category",
    question: "Question",
    answer: "Answer",
    progress: "Progress",
    participant: "Participant",
    session: "Session",
    completed: "Completed",
    inProgress: "In Progress",
    notStarted: "Not Started",
    loading: "Loading assessment..."
  },
  participant: {
    form: {
      title: "Almost Done!",
      subtitle: "Please provide your information to complete the assessment",
      name: "Full Name",
      email: "Email",
      phone: "Phone",
      organization: "Organization",
      position: "Position",
      "position.owner": "Owner",
      "position.coowner": "Co-Owner",
      "position.ceo": "CEO",
      "position.manager": "Manager",
      address: "Address",
      employeeCount: "Number of Employees",
      submit: "Submit Assessment",
      submitting: "Submitting..."
    }
  },
  question: {
    progress: "Question {current} of {total}",
    "all.answered": "All {total} questions answered",
    "keyboard.help": "Press 1, 2, or 3 to select an answer. Arrow left to go back.",
    previous: "Back",
    "final.message": "This is the final question",
    "select.answer": "Select an answer to continue"
  },
  answer: {
    positive: "Yes, usually yes",
    neutral: "Maybe, uncertain",
    negative: "No, usually no"
  },
  budget: {
    title: "Budget",
    type: "Type",
    subject: "Subject",
    section: "Section",
    subjectsAndSections: "Subjects & Sections",
    calculated: "Calculated",
    measure: "Measure",
    planned: "Planned",
    actual: "Actual",
    variance: "Variance",
    variancePercent: "Variance %",
    period: "Period",
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
    quarterly: "Quarterly",
    yearly: "Yearly",
    credit: "Credit",
    debit: "Debit",
    total: "Total",
    profit: "Profit",
    income: "Income",
    expense: "Expense",
    expenses: "Expenses",
    totalIncome: "Total Income",
    totalExpenses: "Total Expenses",
    goal: "Goal",
    budget: "Budget",
    noBudgets: "No Budgets",
    createFirst: "Create your first budget to get started",
    createBudget: "Create Budget",
    allPeriods: "All Periods",
    overview: "Overview",
    trend: "Budget Trend",
    activeProjects: "Active Projects",
    fromIncomes: "% from Incomes",
    fromIncomesGoal: "% from Incomes (Goal)",
    incomePart: "% from Income",
    incomePartGoal: "% Goal",
    measures: "Measures",
    projects: {
      title: "Projects",
      projects: "projects",
      name: "Project Name",
      client: "Client",
      projectManager: "Project Manager",
      manager: "Manager",
      contract: "Contract #",
      status: "Status",
      statusAll: "All Statuses",
      statusPlanning: "Planning",
      statusActive: "Active",
      statusCompleted: "Completed",
      statusFuture: "Future",
      statusCancelled: "Cancelled",
      startDate: "Start Date",
      endDate: "End Date",
      fromDate: "From Date",
      toDate: "To Date",
      budget: "Budget",
      profit: "Profit",
      profitPercent: "Profit %",
      timeConsumed: "Time",
      timeConsumedTooltip: "% of time passed",
      budgetConsumed: "Budget",
      budgetConsumedTooltip: "% of total budget spent",
      invoicesIssued: "Invoices",
      invoicesTooltip: "% of total income collected",
      deleteProject: "Delete Project",
      noProjects: "No Projects Yet",
      noProjectsDescription: "Projects will appear here when created",
      noMatchingProjects: "No Matching Projects",
      adjustFilters: "Try adjusting your filters to see more results",
      createProject: "Create Project",
      typeToSearch: "Type to search...",
      dataTab: "Data",
      chartsTab: "Charts",
      settingsTab: "Settings",
      noData: "No data entries for this project",
      addData: "Add Data",
      addPeriod: "Add Period",
      chartsComingSoon: "Charts coming soon",
      generalInfo: "General Information",
      goals: "Goals",
      goalsDescription: "Set target percentages for each subject/section",
      goalPercent: "Goal %",
      statistics: "Statistics",
      pmStatistics: "By Project Manager",
      managerStatistics: "By Manager",
      clientStatistics: "By Client",
      numberOfProjects: "# Projects",
      totalBudget: "Total Budget",
      totalProfit: "Total Profit",
      incomeExpensesTrend: "Income & Expenses Trend",
      incomeExpensesComparison: "Income vs Expenses",
      incomeDistribution: "Income Distribution",
      expenseDistribution: "Expense Distribution",
      editGoals: "Edit Goals",
      resetToDefaults: "Reset to Defaults"
    }
  },
  planning: {
    title: "Battle Plans",
    newPlan: "New Plan",
    createPlan: "Create Plan",
    battlePlan: "Battle Plan",
    actionItem: "Action Item",
    actionItems: "Action Items",
    participant: "Participant",
    participants: "Participants",
    subjects: "Subjects",
    subjectsAndTasks: "Subjects and Tasks",
    notes: "Notes",
    notesPlaceholder: "Add notes to the plan...",
    dueDate: "Due Date",
    priority: "Priority",
    status: "Status",
    statusDraft: "Draft",
    statusActive: "Active",
    statusCompleted: "Completed",
    statusArchived: "Archived",
    allStatuses: "All Statuses",
    todo: "To Do",
    inProgress: "In Progress",
    done: "Done",
    blocked: "Blocked",
    low: "Low",
    medium: "Medium",
    high: "High",
    urgent: "Urgent",
    priorityLow: "Low Priority",
    priorityMedium: "Medium Priority",
    priorityHigh: "High Priority",
    totalPlans: "Total Plans",
    activePlans: "Active Plans",
    completedTasks: "Completed Tasks",
    overdueTasks: "Overdue Tasks",
    searchPlans: "Search plans...",
    noPlans: "No Plans Yet",
    noPlansDescription: "Create a new battle plan to get started",
    planNotFound: "Plan Not Found",
    backToPlans: "Back to Plans",
    backToList: "Back to list",
    deletePlan: "Delete Plan",
    confirmDelete: "Are you sure you want to delete this plan?",
    createdBy: "Created by",
    progress: "Progress",
    progressLabel: "{completed} of {total} tasks completed",
    noParticipants: "No participants yet",
    addParticipant: "Add Participant",
    participantName: "Name",
    participantEmail: "Email (optional)",
    participantRole: "Role (optional)",
    addSubject: "Add Subject",
    subjectTitle: "Subject title",
    noSubjects: "No subjects yet",
    noSubjectsDescription: "Add a subject to start managing tasks",
    addTask: "Add Task",
    taskDescription: "Task description",
    assignee: "Assignee",
    noTasksInSubject: "No tasks in this subject",
    print: "Print",
    printPlan: "Print Plan",
    loading: "Loading plans...",
    loadingPlan: "Loading plan...",
    calendar: "Calendar",
    addDepartment: "Add Department",
    addMeasure: "Add Measure",
    branchName: "Branch Name",
    collect: "Collect",
    companies: "Companies",
    createFirst: "Create your first plan to get started",
    dashboards: "Dashboards",
    dateRange: "Date Range",
    define: "Define",
    deleteSalesReport: "Delete Sales Report",
    departmentName: "Department Name",
    departments: "Departments",
    employees: "Employees",
    employeesDescription: "Manage company employees",
    explore: "Explore",
    hats: "Hats",
    hatsDescription: "Manage organizational hats and roles",
    intervalType: "Interval Type",
    manageCompanies: "Manage Companies",
    measureName: "Measure Name",
    measures: "Measures",
    metrics: "Metrics",
    metricsDescription: "Track and manage company metrics",
    newSalesReport: "New Sales Report",
    noDashboards: "No dashboards yet",
    noData: "No data available",
    noSalesReports: "No sales reports yet",
    noWidgets: "No widgets configured",
    povs: "POVs",
    povsDescription: "Manage points of view",
    salesReport: "Sales Report",
    spreadType: "Spread Type",
    updateSalesReport: "Update Sales Report",
    widgetName: "Widget Name",
    widgetPlaceholder: "Select a widget...",
    weekdays: {
      sunday: "Sunday",
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday"
    },
    calendarTitle: "Calendar",
    today: "Today",
    activePlan: "Active Plan",
    completed: "Completed",
    highPriorityTask: "High Priority Task",
    mediumPriorityTask: "Medium Priority Task",
    more: "more",
    actionItemsTitle: "Action Items",
    totalTasks: "Total Tasks",
    pending: "Pending",
    overdue: "Overdue",
    searchTasks: "Search tasks...",
    filterAll: "All",
    filterPending: "Pending",
    filterCompleted: "Completed",
    filterOverdue: "Overdue",
    filterToday: "Today",
    filterWeek: "This Week",
    sortByDate: "Sort by date",
    sortByPriority: "Sort by priority",
    sortByPlan: "Sort by plan",
    noPendingTasks: "No pending tasks",
    noCompletedTasks: "No completed tasks",
    noOverdueTasks: "No overdue tasks",
    noTasks: "No tasks",
    priorityHigh: "High",
    priorityMedium: "Medium",
    priorityLow: "Low",
    manageCompany: "Manage Company",
    reports: "Reports",
    visualizations: "Visualizations",
    actionStates: "Action States",
    alerts: "Alerts",
    bizpot: {
      title: "Business Potential Assessment",
      brand: "TurnKeyOS.AI",
      description: "The purpose of this assessment is to identify weak points and problem areas in your business, so we can offer you a business plan to improve revenue and increase efficiency.",
      important: "Important: Please make sure to answer all questions, even those that may seem irrelevant.",
      details: "Details",
      firstName: "First Name",
      lastName: "Last Name",
      title2: "Title",
      company: "Company",
      businessType: "Business Type",
      employeeCount: "Number of Employees",
      email: "Email",
      phone: "Phone",
      startAssessment: "Start Assessment",
      instructions: "Instructions for completing the business analysis questionnaire",
      questionCount: "There are {count} numbered questions. Mark your answer for each one.",
      answerPlus: '+ (plus) = "I agree"',
      answerQuestion: '? (question mark) = "Not sure"',
      answerMinus: '\u2013 (minus) = "I disagree"',
      fontSize: "Font size",
      finish: "Finish",
      unansweredWarning: "Please answer all questions before clicking Finish ({count} remaining)",
      confirmDetails: "Confirm Details",
      submit: "Submit",
      successMessage: "Form submitted successfully"
    },
    ppa: {
      title: "Personal Potential Assessment",
      brand: "TurnKeyOS.AI",
      details: "Details",
      firstName: "First Name",
      lastName: "Last Name",
      company: "Company",
      title2: "Title",
      email: "Email",
      phone: "Phone",
      startAssessment: "Start Assessment",
      instructionsTitle: "Do not begin the questionnaire before reading the instructions",
      instruction1: "Make sure you understand each question: read it as many times as needed. Please answer every question.",
      instruction2: "Do not spend too long on any one question. Answer it as soon as you understand it and move to the next.",
      instruction3: "If the answer would be different for the past versus the present, answer for the present.",
      instruction4plus: '+ (plus) means "I agree", "yes" or "usually yes"',
      instruction4question: '? (question mark) means "not sure", "maybe"',
      instruction4minus: '\u2013 (minus) means "I disagree", "no" or "usually no"',
      fontSize: "Font size",
      submitForm: "Submit Form",
      confirmDetails: "Confirm Details",
      disclaimer: "I confirm that I am the person listed above, I filled in the answers myself and without assistance from another person",
      confirm: "Confirm",
      successMessage: "Form submitted successfully"
    }
  },
  admin: {
    dashboard: "Admin Dashboard",
    platformOverview: "Platform overview and management",
    totalUsers: "Total Users",
    organizations: "Organizations",
    activeAssessments: "Active Assessments",
    completedSessions: "Completed Sessions",
    weeklyActivity: "Weekly Activity",
    topOrganizations: "Top Organizations",
    quickActions: "Quick Actions",
    manageUsers: "Manage Users",
    manageOrganizations: "Manage Organizations",
    createAssessment: "Create Assessment",
    systemSettings: "System Settings",
    sessionsStarted: "Sessions Started",
    completions: "Completions"
  },
  sessions: {
    title: "Assessment Sessions",
    completed: "Completed",
    inProgress: "In Progress",
    abandoned: "Abandoned",
    viewResults: "View Results",
    exportCsv: "Export CSV",
    compare: "Compare",
    participant: "Participant",
    organization: "Organization",
    assessment: "Assessment",
    progress: "Progress",
    status: "Status",
    date: "Date",
    noCompleted: "No Completed Sessions",
    noInProgress: "No Sessions In Progress",
    resultsTitle: "Assessment Results",
    participantInfo: "Participant Information",
    scoreSummary: "Score Summary",
    overallScore: "Overall Score",
    business: "Business",
    personal: "Personal",
    visualization: "Results Visualization",
    progressBars: "Progress Bars",
    radarChart: "Radar Chart",
    gauges: "Gauges",
    categoryAnalysis: "Category Analysis",
    excellent: "Excellent",
    good: "Good",
    fair: "Fair",
    needsWork: "Needs Work",
    backToSessions: "Back to Sessions",
    printResults: "Print Results",
    downloadPdf: "Download PDF"
  },
  orgboard: {
    title: "Organization Board",
    employees: "Employees",
    divisions: "Divisions",
    departments: "Departments",
    sections: "Sections",
    functions: "Functions",
    addEmployee: "Add Employee",
    addDivision: "Add Division",
    addDepartment: "Add Department",
    addSection: "Add Section",
    addFunction: "Add Function",
    editMode: "Edit Mode",
    viewMode: "View Mode",
    exportPng: "Export PNG",
    exportSvg: "Export SVG",
    exportJson: "Export JSON",
    importJson: "Import JSON",
    print: "Print",
    companySetup: "Company Setup",
    vfp: "Valuable Final Product",
    manager: "Manager",
    noManager: "No Manager",
    selectManager: "Select Manager"
  },
  controls: {
    zoom: "Zoom",
    textSize: "Text Size",
    divisionWidth: "Division Width",
    fitToScreen: "Fit to Screen",
    layout: "Layout",
    editMode: "Edit Mode",
    viewerNoEdit: "Viewer (no edit)",
    showSections: "Show Sections",
    showFunctions: "Show Functions",
    showDetails: "Show Details",
    companySetup: "Company Setup",
    filterByDivision: "Filter by Division",
    filterByDepartment: "Filter by Department",
    allDivisions: "All Divisions",
    allDepartments: "All Departments"
  },
  nav: {
    employees: "Employees",
    admin: "Admin",
    logout: "Logout",
    dashboard: "Dashboard",
    projects: "Projects",
    settings: "Settings",
    data: "Data",
    charts: "Charts"
  },
  categories: {
    management: "Management",
    organization: "Organization",
    repeatSales: "Repeat Sales",
    finance: "Finance",
    operations: "Operations",
    quality: "Quality Control",
    newSales: "New Sales",
    stress: "Stress",
    principles: "Principles",
    help: "Help Acceptance"
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    network: "Network error. Please check your connection.",
    unauthorized: "You are not authorized to perform this action.",
    notFound: "The requested resource was not found.",
    validation: "Please check the form for errors.",
    noOrganization: "No Organization Assigned",
    contactAdmin: "Please contact your administrator to be assigned to an organization.",
    loginFailed: "Login failed. Please check your credentials.",
    sessionFailed: "Failed to create assessment session",
    sessionNotReady: "Session not ready. Please wait a moment.",
    submissionFailed: "Failed to submit assessment. Please try again."
  },
  validation: {
    required: "This field is required",
    email: "Please enter a valid email address",
    minLength: "Must be at least {min} characters",
    maxLength: "Must be at most {max} characters",
    pattern: "Invalid format"
  },
  settings: {
    budgetSettings: "Budget Settings",
    budgetName: "Budget Name",
    interval: "Interval",
    period: "Period",
    subjects: "Subjects",
    addSubject: "Add Subject",
    addSection: "Add Section",
    addFirstSection: "Add first section",
    noSubjects: "No subjects defined",
    enterSubjectName: "Enter subject name:",
    enterSectionName: "Enter section name:",
    isIncome: "Is this an income category? (OK=Yes, Cancel=No)",
    weekly: "Weekly",
    monthly: "Monthly",
    quarterly: "Quarterly",
    yearly: "Yearly",
    general: "General",
    name: "Name",
    intervalType: "Interval Type",
    startDate: "Start Date",
    endDate: "End Date",
    goals: "Goals",
    measures: "Measures",
    credit: "Credit",
    debit: "Debit",
    calculated: "Calculated",
    updateBudget: "Update Budget",
    createDataCollection: "Create Data Collection",
    deleteBudget: "Delete Budget",
    duplicate: "Duplicate",
    confirmDeleteBudget: "Are you sure you want to delete this budget?",
    createBudgetError: "Error creating budget",
    createNewBudget: "New Budget",
    currency: "Currency",
    enterBudgetName: "Enter budget name:"
  },
  actions: {
    create: "Create",
    cancel: "Cancel",
    delete: "Delete",
    confirmDelete: "Are you sure you want to delete this item?",
    edit: "Edit",
    moveUp: "Move Up",
    moveDown: "Move Down"
  }
};

// src/i18n/dictionaries/he.json
var he_default = {
  general: {
    loading: "\u05D8\u05D5\u05E2\u05DF...",
    saving: "\u05E9\u05D5\u05DE\u05E8...",
    error: "\u05D0\u05D9\u05E8\u05E2\u05D4 \u05E9\u05D2\u05D9\u05D0\u05D4",
    success: "\u05D4\u05E6\u05DC\u05D7\u05D4",
    comingSoon: "\u05D1\u05E7\u05E8\u05D5\u05D1"
  },
  login: {
    title: "\u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA",
    subtitle: "\u05D4\u05EA\u05D7\u05D1\u05E8 \u05DC\u05D7\u05E9\u05D1\u05D5\u05DF \u05E9\u05DC\u05DA",
    email: "\u05D0\u05D9\u05DE\u05D9\u05D9\u05DC",
    emailPlaceholder: "\u05D4\u05DB\u05E0\u05E1 \u05D0\u05EA \u05D4\u05D0\u05D9\u05DE\u05D9\u05D9\u05DC \u05E9\u05DC\u05DA",
    password: "\u05E1\u05D9\u05E1\u05DE\u05D4",
    passwordPlaceholder: "\u05D4\u05DB\u05E0\u05E1 \u05D0\u05EA \u05D4\u05E1\u05D9\u05E1\u05DE\u05D4 \u05E9\u05DC\u05DA",
    signIn: "\u05D4\u05EA\u05D7\u05D1\u05E8",
    or: "\u05D0\u05D5",
    continueWithGoogle: "\u05D4\u05EA\u05D7\u05D1\u05E8 \u05E2\u05DD Google",
    continueWithMicrosoft: "\u05D4\u05EA\u05D7\u05D1\u05E8 \u05E2\u05DD Microsoft",
    forgotPassword: "\u05E9\u05DB\u05D7\u05EA \u05E1\u05D9\u05E1\u05DE\u05D4?",
    needHelp: "\u05E6\u05E8\u05D9\u05DA \u05E2\u05D6\u05E8\u05D4?",
    contactSupport: "\u05E6\u05D5\u05E8 \u05E7\u05E9\u05E8 \u05E2\u05DD \u05D4\u05EA\u05DE\u05D9\u05DB\u05D4",
    noAccount: "\u05D0\u05D9\u05DF \u05DC\u05DA \u05D7\u05E9\u05D1\u05D5\u05DF?",
    register: "\u05D4\u05D9\u05E8\u05E9\u05DD"
  },
  common: {
    add: "\u05D4\u05D5\u05E1\u05E3",
    edit: "\u05E2\u05E8\u05D5\u05DA",
    delete: "\u05DE\u05D7\u05E7",
    cancel: "\u05D1\u05D9\u05D8\u05D5\u05DC",
    save: "\u05E9\u05DE\u05D5\u05E8",
    update: "\u05E2\u05D3\u05DB\u05DF",
    submit: "\u05E9\u05DC\u05D7",
    search: "\u05D7\u05E4\u05E9",
    filter: "\u05E1\u05E0\u05DF",
    reset: "\u05D0\u05E4\u05E1",
    clear: "\u05E0\u05E7\u05D4",
    close: "\u05E1\u05D2\u05D5\u05E8",
    back: "\u05D7\u05D6\u05D5\u05E8",
    next: "\u05D4\u05D1\u05D0",
    previous: "\u05D4\u05E7\u05D5\u05D3\u05DD",
    loading: "\u05D8\u05D5\u05E2\u05DF...",
    saving: "\u05E9\u05D5\u05DE\u05E8...",
    noData: "\u05D0\u05D9\u05DF \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD",
    error: "\u05D0\u05D9\u05E8\u05E2\u05D4 \u05E9\u05D2\u05D9\u05D0\u05D4",
    success: "\u05D4\u05E6\u05DC\u05D7\u05D4",
    confirm: "\u05D0\u05D9\u05E9\u05D5\u05E8",
    yes: "\u05DB\u05DF",
    no: "\u05DC\u05D0",
    all: "\u05D4\u05DB\u05DC",
    none: "\u05DC\u05DC\u05D0",
    select: "\u05D1\u05D7\u05E8",
    required: "\u05E9\u05D3\u05D4 \u05D7\u05D5\u05D1\u05D4",
    optional: "\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9",
    actions: "\u05E4\u05E2\u05D5\u05DC\u05D5\u05EA",
    details: "\u05E4\u05E8\u05D8\u05D9\u05DD",
    settings: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA",
    help: "\u05E2\u05D6\u05E8\u05D4",
    logout: "\u05D4\u05EA\u05E0\u05EA\u05E7",
    login: "\u05D4\u05EA\u05D7\u05D1\u05E8",
    signUp: "\u05D4\u05E8\u05E9\u05DE\u05D4",
    welcome: "\u05D1\u05E8\u05D5\u05DA \u05D4\u05D1\u05D0",
    home: "\u05D1\u05D9\u05EA",
    dashboard: "\u05DC\u05D5\u05D7 \u05D1\u05E7\u05E8\u05D4",
    profile: "\u05E4\u05E8\u05D5\u05E4\u05D9\u05DC",
    notifications: "\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA",
    export: "\u05D9\u05D9\u05E6\u05D5\u05D0",
    import: "\u05D9\u05D9\u05D1\u05D5\u05D0",
    download: "\u05D4\u05D5\u05E8\u05D3",
    upload: "\u05D4\u05E2\u05DC\u05D4",
    print: "\u05D4\u05D3\u05E4\u05E1",
    share: "\u05E9\u05EA\u05E3",
    showing: "\u05DE\u05E6\u05D9\u05D2",
    of: "\u05DE\u05EA\u05D5\u05DA",
    perPage: "\u05D1\u05E2\u05DE\u05D5\u05D3",
    clearFilters: "\u05E0\u05E7\u05D4 \u05E1\u05D9\u05E0\u05D5\u05DF",
    confirmDelete: "\u05D4\u05D0\u05DD \u05D0\u05EA\u05D4 \u05D1\u05D8\u05D5\u05D7 \u05E9\u05D1\u05E8\u05E6\u05D5\u05E0\u05DA \u05DC\u05DE\u05D7\u05D5\u05E7?",
    total: "\u05E1\u05D4\u05F4\u05DB",
    active: "\u05E4\u05E2\u05D9\u05DC",
    addNew: "\u05D4\u05D5\u05E1\u05E3 \u05D7\u05D3\u05E9",
    contactPerson: "\u05D0\u05D9\u05E9 \u05E7\u05E9\u05E8",
    email: "\u05D3\u05D5\u05D0\u05F4\u05DC",
    name: "\u05E9\u05DD",
    owner: "\u05D1\u05E2\u05DC\u05D9\u05DD",
    phone: "\u05D8\u05DC\u05E4\u05D5\u05DF",
    size: "\u05D2\u05D5\u05D3\u05DC",
    status: "\u05E1\u05D8\u05D8\u05D5\u05E1",
    description: "\u05EA\u05D9\u05D0\u05D5\u05E8",
    title: "\u05DB\u05D5\u05EA\u05E8\u05EA",
    company: "\u05D7\u05D1\u05E8\u05D4",
    address: "\u05DB\u05EA\u05D5\u05D1\u05EA",
    website: "\u05D0\u05EA\u05E8 \u05D0\u05D9\u05E0\u05D8\u05E8\u05E0\u05D8"
  },
  auth: {
    signIn: "\u05D4\u05EA\u05D7\u05D1\u05E8",
    signOut: "\u05D4\u05EA\u05E0\u05EA\u05E7",
    signUp: "\u05D4\u05E8\u05E9\u05DE\u05D4",
    signInWithGoogle: "\u05D4\u05EA\u05D7\u05D1\u05E8 \u05E2\u05DD Google",
    email: "\u05D3\u05D5\u05D0\u05E8 \u05D0\u05DC\u05E7\u05D8\u05E8\u05D5\u05E0\u05D9",
    password: "\u05E1\u05D9\u05E1\u05DE\u05D4",
    confirmPassword: "\u05D0\u05E9\u05E8 \u05E1\u05D9\u05E1\u05DE\u05D4",
    forgotPassword: "\u05E9\u05DB\u05D7\u05EA \u05E1\u05D9\u05E1\u05DE\u05D4?",
    resetPassword: "\u05D0\u05D9\u05E4\u05D5\u05E1 \u05E1\u05D9\u05E1\u05DE\u05D4",
    rememberMe: "\u05D6\u05DB\u05D5\u05E8 \u05D0\u05D5\u05EA\u05D9",
    newUser: "\u05DE\u05E9\u05EA\u05DE\u05E9 \u05D7\u05D3\u05E9?",
    alreadyHaveAccount: "\u05DB\u05D1\u05E8 \u05D9\u05E9 \u05DC\u05DA \u05D7\u05E9\u05D1\u05D5\u05DF?",
    createAccount: "\u05E6\u05D5\u05E8 \u05D7\u05E9\u05D1\u05D5\u05DF",
    invalidCredentials: "\u05D3\u05D5\u05D0\u05E8 \u05D0\u05DC\u05E7\u05D8\u05E8\u05D5\u05E0\u05D9 \u05D0\u05D5 \u05E1\u05D9\u05E1\u05DE\u05D4 \u05DC\u05D0 \u05E0\u05DB\u05D5\u05E0\u05D9\u05DD",
    emailRequired: "\u05D3\u05D5\u05D0\u05E8 \u05D0\u05DC\u05E7\u05D8\u05E8\u05D5\u05E0\u05D9 \u05E0\u05D3\u05E8\u05E9",
    passwordRequired: "\u05E1\u05D9\u05E1\u05DE\u05D4 \u05E0\u05D3\u05E8\u05E9\u05EA",
    passwordMismatch: "\u05D4\u05E1\u05D9\u05E1\u05DE\u05D0\u05D5\u05EA \u05D0\u05D9\u05E0\u05DF \u05EA\u05D5\u05D0\u05DE\u05D5\u05EA"
  },
  navigation: {
    assessments: "\u05D4\u05E2\u05E8\u05DB\u05D5\u05EA",
    admin: "\u05E0\u05D9\u05D4\u05D5\u05DC",
    orgBoard: "\u05DC\u05D5\u05D7 \u05D0\u05E8\u05D2\u05D5\u05DF",
    budget: "\u05EA\u05E7\u05E6\u05D9\u05D1",
    planning: "\u05EA\u05DB\u05E0\u05D5\u05DF",
    reports: "\u05D3\u05D5\u05D7\u05D5\u05EA",
    users: "\u05DE\u05E9\u05EA\u05DE\u05E9\u05D9\u05DD",
    organizations: "\u05D0\u05E8\u05D2\u05D5\u05E0\u05D9\u05DD",
    settings: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA"
  },
  user: {
    name: "\u05E9\u05DD",
    email: "\u05D3\u05D5\u05D0\u05E8 \u05D0\u05DC\u05E7\u05D8\u05E8\u05D5\u05E0\u05D9",
    role: "\u05EA\u05E4\u05E7\u05D9\u05D3",
    organization: "\u05D0\u05E8\u05D2\u05D5\u05DF",
    phone: "\u05D8\u05DC\u05E4\u05D5\u05DF",
    position: "\u05DE\u05E9\u05E8\u05D4",
    department: "\u05DE\u05D7\u05DC\u05E7\u05D4",
    lastLogin: "\u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA \u05D0\u05D7\u05E8\u05D5\u05E0\u05D4",
    createdAt: "\u05E0\u05D5\u05E6\u05E8 \u05D1\u05EA\u05D0\u05E8\u05D9\u05DA",
    status: "\u05E1\u05D8\u05D8\u05D5\u05E1",
    active: "\u05E4\u05E2\u05D9\u05DC",
    inactive: "\u05DC\u05D0 \u05E4\u05E2\u05D9\u05DC"
  },
  roles: {
    admin: "\u05DE\u05E0\u05D4\u05DC \u05DE\u05E2\u05E8\u05DB\u05EA",
    org_admin: "\u05DE\u05E0\u05D4\u05DC \u05D0\u05E8\u05D2\u05D5\u05DF",
    org_user: "\u05DE\u05E9\u05EA\u05DE\u05E9 \u05D0\u05E8\u05D2\u05D5\u05DF",
    firm_lead: "\u05DE\u05E0\u05D4\u05DC \u05D7\u05D1\u05E8\u05EA \u05D9\u05D9\u05E2\u05D5\u05E5",
    consultant: "\u05D9\u05D5\u05E2\u05E5",
    viewer: "\u05E6\u05D5\u05E4\u05D4"
  },
  organization: {
    name: "\u05E9\u05DD \u05D4\u05D0\u05E8\u05D2\u05D5\u05DF",
    divisions: "\u05D0\u05D2\u05E4\u05D9\u05DD",
    departments: "\u05DE\u05D7\u05DC\u05E7\u05D5\u05EA",
    employees: "\u05E2\u05D5\u05D1\u05D3\u05D9\u05DD",
    createOrg: "\u05E6\u05D5\u05E8 \u05D0\u05E8\u05D2\u05D5\u05DF",
    editOrg: "\u05E2\u05E8\u05D5\u05DA \u05D0\u05E8\u05D2\u05D5\u05DF",
    orgBoard: "\u05DC\u05D5\u05D7 \u05D0\u05E8\u05D2\u05D5\u05DF",
    noOrganization: "\u05DC\u05D0 \u05DE\u05E9\u05D5\u05D9\u05DA \u05DC\u05D0\u05E8\u05D2\u05D5\u05DF"
  },
  assessment: {
    title: "\u05D4\u05E2\u05E8\u05DB\u05D4",
    type: "\u05E1\u05D5\u05D2",
    start: "\u05D4\u05EA\u05D7\u05DC \u05D4\u05E2\u05E8\u05DB\u05D4",
    continue: "\u05D4\u05DE\u05E9\u05DA",
    complete: "\u05D4\u05E9\u05DC\u05DD",
    results: "\u05EA\u05D5\u05E6\u05D0\u05D5\u05EA",
    score: "\u05E6\u05D9\u05D5\u05DF",
    category: "\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D4",
    question: "\u05E9\u05D0\u05DC\u05D4",
    answer: "\u05EA\u05E9\u05D5\u05D1\u05D4",
    progress: "\u05D4\u05EA\u05E7\u05D3\u05DE\u05D5\u05EA",
    participant: "\u05DE\u05E9\u05EA\u05EA\u05E3",
    session: "\u05E1\u05E9\u05DF",
    completed: "\u05D4\u05D5\u05E9\u05DC\u05DD",
    inProgress: "\u05D1\u05EA\u05D4\u05DC\u05D9\u05DA",
    notStarted: "\u05DC\u05D0 \u05D4\u05EA\u05D7\u05D9\u05DC",
    loading: "\u05D8\u05D5\u05E2\u05DF \u05D4\u05E2\u05E8\u05DB\u05D4..."
  },
  participant: {
    form: {
      title: "\u05DB\u05DE\u05E2\u05D8 \u05E1\u05D9\u05D9\u05DE\u05E0\u05D5!",
      subtitle: "\u05D0\u05E0\u05D0 \u05DE\u05DC\u05D0 \u05D0\u05EA \u05D4\u05E4\u05E8\u05D8\u05D9\u05DD \u05E9\u05DC\u05DA \u05DC\u05D4\u05E9\u05DC\u05DE\u05EA \u05D4\u05D4\u05E2\u05E8\u05DB\u05D4",
      name: "\u05E9\u05DD \u05DE\u05DC\u05D0",
      email: "\u05D3\u05D5\u05D0\u05E8 \u05D0\u05DC\u05E7\u05D8\u05E8\u05D5\u05E0\u05D9",
      phone: "\u05D8\u05DC\u05E4\u05D5\u05DF",
      organization: "\u05D0\u05E8\u05D2\u05D5\u05DF",
      position: "\u05EA\u05E4\u05E7\u05D9\u05D3",
      "position.owner": "\u05D1\u05E2\u05DC\u05D9\u05DD",
      "position.coowner": "\u05E9\u05D5\u05EA\u05E3",
      "position.ceo": "\u05DE\u05E0\u05DB\u05F4\u05DC",
      "position.manager": "\u05DE\u05E0\u05D4\u05DC",
      address: "\u05DB\u05EA\u05D5\u05D1\u05EA",
      employeeCount: "\u05DE\u05E1\u05E4\u05E8 \u05E2\u05D5\u05D1\u05D3\u05D9\u05DD",
      submit: "\u05E9\u05DC\u05D7 \u05D4\u05E2\u05E8\u05DB\u05D4",
      submitting: "\u05E9\u05D5\u05DC\u05D7..."
    }
  },
  question: {
    progress: "\u05E9\u05D0\u05DC\u05D4 {current} \u05DE\u05EA\u05D5\u05DA {total}",
    "all.answered": "\u05DB\u05DC {total} \u05D4\u05E9\u05D0\u05DC\u05D5\u05EA \u05E0\u05E2\u05E0\u05D5",
    "keyboard.help": "\u05D4\u05E7\u05E9 1, 2 \u05D0\u05D5 3 \u05DC\u05D1\u05D7\u05D9\u05E8\u05EA \u05EA\u05E9\u05D5\u05D1\u05D4. \u05D7\u05E5 \u05E9\u05DE\u05D0\u05DC\u05D4 \u05DC\u05D7\u05D6\u05E8\u05D4.",
    previous: "\u05D4\u05E7\u05D5\u05D3\u05DD",
    "final.message": "\u05D6\u05D5 \u05D4\u05E9\u05D0\u05DC\u05D4 \u05D4\u05D0\u05D7\u05E8\u05D5\u05E0\u05D4",
    "select.answer": "\u05D1\u05D7\u05E8 \u05EA\u05E9\u05D5\u05D1\u05D4 \u05DC\u05D4\u05DE\u05E9\u05DA"
  },
  answer: {
    positive: "\u05DB\u05DF, \u05D1\u05D3\u05E8\u05DA-\u05DB\u05DC\u05DC \u05DB\u05DF",
    neutral: "\u05DC\u05D0 \u05D1\u05D8\u05D5\u05D7, \u05D0\u05D5\u05DC\u05D9",
    negative: "\u05DC\u05D0. \u05D1\u05D3\u05E8\u05DA \u05DB\u05DC\u05DC \u05DC\u05D0"
  },
  budget: {
    title: "\u05EA\u05E7\u05E6\u05D9\u05D1",
    type: "\u05E1\u05D5\u05D2",
    subject: "\u05E0\u05D5\u05E9\u05D0",
    section: "\u05E1\u05E2\u05D9\u05E3",
    subjectsAndSections: "\u05E0\u05D5\u05E9\u05D0\u05D9\u05DD \u05D5\u05E1\u05E2\u05D9\u05E4\u05D9\u05DD",
    calculated: "\u05DE\u05D7\u05D5\u05E9\u05D1",
    measure: "\u05DE\u05D3\u05D3",
    planned: "\u05DE\u05EA\u05D5\u05DB\u05E0\u05DF",
    actual: "\u05D1\u05E4\u05D5\u05E2\u05DC",
    variance: "\u05E1\u05D8\u05D9\u05D9\u05D4",
    variancePercent: "\u05D0\u05D7\u05D5\u05D6 \u05E1\u05D8\u05D9\u05D9\u05D4",
    period: "\u05EA\u05E7\u05D5\u05E4\u05D4",
    daily: "\u05D9\u05D5\u05DE\u05D9",
    weekly: "\u05E9\u05D1\u05D5\u05E2\u05D9",
    monthly: "\u05D7\u05D5\u05D3\u05E9\u05D9",
    quarterly: "\u05E8\u05D1\u05E2\u05D5\u05E0\u05D9",
    yearly: "\u05E9\u05E0\u05EA\u05D9",
    credit: "\u05D6\u05DB\u05D5\u05EA",
    debit: "\u05D7\u05D5\u05D1\u05D4",
    total: "\u05E1\u05D4\u05F4\u05DB",
    profit: "\u05E8\u05D5\u05D5\u05D7",
    income: "\u05D4\u05DB\u05E0\u05E1\u05D4",
    expense: "\u05D4\u05D5\u05E6\u05D0\u05D4",
    expenses: "\u05D4\u05D5\u05E6\u05D0\u05D5\u05EA",
    totalIncome: "\u05E1\u05D4\u05F4\u05DB \u05D4\u05DB\u05E0\u05E1\u05D5\u05EA",
    totalExpenses: "\u05E1\u05D4\u05F4\u05DB \u05D4\u05D5\u05E6\u05D0\u05D5\u05EA",
    goal: "\u05D9\u05E2\u05D3",
    budget: "\u05EA\u05E7\u05E6\u05D9\u05D1",
    noBudgets: "\u05D0\u05D9\u05DF \u05EA\u05E7\u05E6\u05D9\u05D1\u05D9\u05DD",
    createFirst: "\u05E6\u05D5\u05E8 \u05D0\u05EA \u05D4\u05EA\u05E7\u05E6\u05D9\u05D1 \u05D4\u05E8\u05D0\u05E9\u05D5\u05DF \u05E9\u05DC\u05DA \u05DC\u05D4\u05EA\u05D7\u05D9\u05DC",
    createBudget: "\u05E6\u05D5\u05E8 \u05EA\u05E7\u05E6\u05D9\u05D1",
    allPeriods: "\u05DB\u05DC \u05D4\u05EA\u05E7\u05D5\u05E4\u05D5\u05EA",
    overview: "\u05E1\u05E7\u05D9\u05E8\u05D4",
    trend: "\u05DE\u05D2\u05DE\u05EA \u05EA\u05E7\u05E6\u05D9\u05D1",
    activeProjects: "\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD \u05E4\u05E2\u05D9\u05DC\u05D9\u05DD",
    fromIncomes: "% \u05DE\u05D4\u05D4\u05DB\u05E0\u05E1\u05D5\u05EA",
    fromIncomesGoal: "% \u05DE\u05D4\u05D4\u05DB\u05E0\u05E1\u05D5\u05EA (\u05D9\u05E2\u05D3)",
    incomePart: "% \u05DE\u05D4\u05DB\u05E0\u05E1\u05D5\u05EA",
    incomePartGoal: "% \u05D9\u05E2\u05D3",
    measures: "\u05DE\u05D3\u05D3\u05D9\u05DD",
    projects: {
      title: "\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD",
      projects: "\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD",
      name: "\u05E9\u05DD \u05D4\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8",
      client: "\u05DC\u05E7\u05D5\u05D7",
      projectManager: "\u05DE\u05E0\u05D4\u05DC \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8",
      manager: "\u05DE\u05E0\u05D4\u05DC",
      contract: "\u05D7\u05D5\u05D6\u05D4 \u05DE\u05E1\u05F3",
      status: "\u05E1\u05D8\u05D8\u05D5\u05E1",
      statusAll: "\u05DB\u05DC \u05D4\u05E1\u05D8\u05D8\u05D5\u05E1\u05D9\u05DD",
      statusPlanning: "\u05D1\u05EA\u05DB\u05E0\u05D5\u05DF",
      statusActive: "\u05E4\u05E2\u05D9\u05DC",
      statusCompleted: "\u05D4\u05D5\u05E9\u05DC\u05DD",
      statusFuture: "\u05E2\u05EA\u05D9\u05D3\u05D9",
      statusCancelled: "\u05D1\u05D5\u05D8\u05DC",
      startDate: "\u05EA\u05D0\u05E8\u05D9\u05DA \u05D4\u05EA\u05D7\u05DC\u05D4",
      endDate: "\u05EA\u05D0\u05E8\u05D9\u05DA \u05E1\u05D9\u05D5\u05DD",
      fromDate: "\u05DE\u05EA\u05D0\u05E8\u05D9\u05DA",
      toDate: "\u05E2\u05D3 \u05EA\u05D0\u05E8\u05D9\u05DA",
      budget: "\u05EA\u05E7\u05E6\u05D9\u05D1",
      profit: "\u05E8\u05D5\u05D5\u05D7",
      profitPercent: "\u05D0\u05D7\u05D5\u05D6 \u05E8\u05D5\u05D5\u05D7",
      timeConsumed: "\u05D6\u05DE\u05DF",
      timeConsumedTooltip: "% \u05DE\u05D4\u05D6\u05DE\u05DF \u05E9\u05D7\u05DC\u05E3",
      budgetConsumed: "\u05EA\u05E7\u05E6\u05D9\u05D1",
      budgetConsumedTooltip: "% \u05DE\u05D4\u05EA\u05E7\u05E6\u05D9\u05D1 \u05D4\u05DB\u05D5\u05DC\u05DC \u05E9\u05D4\u05D5\u05E6\u05D0",
      invoicesIssued: "\u05D7\u05E9\u05D1\u05D5\u05E0\u05D9\u05D5\u05EA",
      invoicesTooltip: "% \u05DE\u05E1\u05D4\u05F4\u05DB \u05D4\u05D4\u05DB\u05E0\u05E1\u05D5\u05EA \u05E9\u05E0\u05D2\u05D1\u05D5",
      deleteProject: "\u05DE\u05D7\u05E7 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8",
      noProjects: "\u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD",
      noProjectsDescription: "\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD \u05D9\u05D5\u05E4\u05D9\u05E2\u05D5 \u05DB\u05D0\u05DF \u05DC\u05D0\u05D7\u05E8 \u05D9\u05E6\u05D9\u05E8\u05EA\u05DD",
      noMatchingProjects: "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD \u05EA\u05D5\u05D0\u05DE\u05D9\u05DD",
      adjustFilters: "\u05E0\u05E1\u05D4 \u05DC\u05D4\u05EA\u05D0\u05D9\u05DD \u05D0\u05EA \u05D4\u05E1\u05D9\u05E0\u05D5\u05DF \u05DB\u05D3\u05D9 \u05DC\u05E8\u05D0\u05D5\u05EA \u05D9\u05D5\u05EA\u05E8 \u05EA\u05D5\u05E6\u05D0\u05D5\u05EA",
      createProject: "\u05E6\u05D5\u05E8 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8",
      typeToSearch: "\u05D4\u05E7\u05DC\u05D3 \u05DC\u05D7\u05D9\u05E4\u05D5\u05E9...",
      dataTab: "\u05E0\u05EA\u05D5\u05E0\u05D9\u05DD",
      chartsTab: "\u05D2\u05E8\u05E4\u05D9\u05DD",
      settingsTab: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA",
      noData: "\u05D0\u05D9\u05DF \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD \u05DC\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8 \u05D6\u05D4",
      addData: "\u05D4\u05D5\u05E1\u05E3 \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD",
      addPeriod: "\u05D4\u05D5\u05E1\u05E3 \u05EA\u05E7\u05D5\u05E4\u05D4",
      chartsComingSoon: "\u05D2\u05E8\u05E4\u05D9\u05DD \u05D1\u05E7\u05E8\u05D5\u05D1",
      generalInfo: "\u05DE\u05D9\u05D3\u05E2 \u05DB\u05DC\u05DC\u05D9",
      goals: "\u05D9\u05E2\u05D3\u05D9\u05DD",
      goalsDescription: "\u05D4\u05D2\u05D3\u05E8 \u05D0\u05D7\u05D5\u05D6\u05D9 \u05D9\u05E2\u05D3 \u05DC\u05DB\u05DC \u05E0\u05D5\u05E9\u05D0/\u05E1\u05E2\u05D9\u05E3",
      goalPercent: "\u05D0\u05D7\u05D5\u05D6 \u05D9\u05E2\u05D3",
      statistics: "\u05E1\u05D8\u05D8\u05D9\u05E1\u05D8\u05D9\u05E7\u05D4",
      pmStatistics: "\u05DC\u05E4\u05D9 \u05DE\u05E0\u05D4\u05DC \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8",
      managerStatistics: "\u05DC\u05E4\u05D9 \u05DE\u05E0\u05D4\u05DC",
      clientStatistics: "\u05DC\u05E4\u05D9 \u05DC\u05E7\u05D5\u05D7",
      numberOfProjects: "\u05DE\u05E1\u05F3 \u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD",
      totalBudget: "\u05E1\u05D4\u05F4\u05DB \u05EA\u05E7\u05E6\u05D9\u05D1",
      totalProfit: "\u05E1\u05D4\u05F4\u05DB \u05E8\u05D5\u05D5\u05D7",
      incomeExpensesTrend: "\u05DE\u05D2\u05DE\u05EA \u05D4\u05DB\u05E0\u05E1\u05D5\u05EA \u05D5\u05D4\u05D5\u05E6\u05D0\u05D5\u05EA",
      incomeExpensesComparison: "\u05D4\u05DB\u05E0\u05E1\u05D5\u05EA \u05DE\u05D5\u05DC \u05D4\u05D5\u05E6\u05D0\u05D5\u05EA",
      incomeDistribution: "\u05E4\u05D9\u05DC\u05D5\u05D7 \u05D4\u05DB\u05E0\u05E1\u05D5\u05EA",
      expenseDistribution: "\u05E4\u05D9\u05DC\u05D5\u05D7 \u05D4\u05D5\u05E6\u05D0\u05D5\u05EA",
      editGoals: "\u05E2\u05E8\u05D9\u05DB\u05EA \u05D9\u05E2\u05D3\u05D9\u05DD",
      resetToDefaults: "\u05D0\u05D9\u05E4\u05D5\u05E1 \u05DC\u05D1\u05E8\u05D9\u05E8\u05EA \u05DE\u05D7\u05D3\u05DC"
    }
  },
  planning: {
    title: "\u05EA\u05D5\u05DB\u05E0\u05D9\u05D5\u05EA \u05E7\u05E8\u05D1",
    newPlan: "\u05EA\u05D5\u05DB\u05E0\u05D9\u05EA \u05D7\u05D3\u05E9\u05D4",
    createPlan: "\u05E6\u05D5\u05E8 \u05EA\u05D5\u05DB\u05E0\u05D9\u05EA",
    battlePlan: "\u05EA\u05D5\u05DB\u05E0\u05D9\u05EA \u05E7\u05E8\u05D1",
    actionItem: "\u05DE\u05E9\u05D9\u05DE\u05D4",
    actionItems: "\u05DE\u05E9\u05D9\u05DE\u05D5\u05EA",
    participant: "\u05DE\u05E9\u05EA\u05EA\u05E3",
    participants: "\u05DE\u05E9\u05EA\u05EA\u05E4\u05D9\u05DD",
    subjects: "\u05E0\u05D5\u05E9\u05D0\u05D9\u05DD",
    subjectsAndTasks: "\u05E0\u05D5\u05E9\u05D0\u05D9\u05DD \u05D5\u05DE\u05E9\u05D9\u05DE\u05D5\u05EA",
    notes: "\u05D4\u05E2\u05E8\u05D5\u05EA",
    notesPlaceholder: "\u05D4\u05D5\u05E1\u05E3 \u05D4\u05E2\u05E8\u05D5\u05EA \u05DC\u05EA\u05D5\u05DB\u05E0\u05D9\u05EA...",
    dueDate: "\u05EA\u05D0\u05E8\u05D9\u05DA \u05D9\u05E2\u05D3",
    priority: "\u05E2\u05D3\u05D9\u05E4\u05D5\u05EA",
    status: "\u05E1\u05D8\u05D8\u05D5\u05E1",
    statusDraft: "\u05D8\u05D9\u05D5\u05D8\u05D4",
    statusActive: "\u05E4\u05E2\u05D9\u05DC",
    statusCompleted: "\u05D4\u05D5\u05E9\u05DC\u05DD",
    statusArchived: "\u05D0\u05E8\u05DB\u05D9\u05D5\u05DF",
    allStatuses: "\u05DB\u05DC \u05D4\u05E1\u05D8\u05D8\u05D5\u05E1\u05D9\u05DD",
    todo: "\u05DC\u05D1\u05D9\u05E6\u05D5\u05E2",
    inProgress: "\u05D1\u05EA\u05D4\u05DC\u05D9\u05DA",
    done: "\u05D1\u05D5\u05E6\u05E2",
    blocked: "\u05D7\u05E1\u05D5\u05DD",
    low: "\u05E0\u05DE\u05D5\u05DB\u05D4",
    medium: "\u05D1\u05D9\u05E0\u05D5\u05E0\u05D9\u05EA",
    high: "\u05D2\u05D1\u05D5\u05D4\u05D4",
    urgent: "\u05D3\u05D7\u05D5\u05E3",
    priorityLow: "\u05E2\u05D3\u05D9\u05E4\u05D5\u05EA \u05E0\u05DE\u05D5\u05DB\u05D4",
    priorityMedium: "\u05E2\u05D3\u05D9\u05E4\u05D5\u05EA \u05D1\u05D9\u05E0\u05D5\u05E0\u05D9\u05EA",
    priorityHigh: "\u05E2\u05D3\u05D9\u05E4\u05D5\u05EA \u05D2\u05D1\u05D5\u05D4\u05D4",
    totalPlans: "\u05E1\u05D4\u05F4\u05DB \u05EA\u05D5\u05DB\u05E0\u05D9\u05D5\u05EA",
    activePlans: "\u05E4\u05E2\u05D9\u05DC\u05D5\u05EA",
    completedTasks: "\u05DE\u05E9\u05D9\u05DE\u05D5\u05EA \u05E9\u05D4\u05D5\u05E9\u05DC\u05DE\u05D5",
    overdueTasks: "\u05DE\u05E9\u05D9\u05DE\u05D5\u05EA \u05D1\u05D0\u05D9\u05D7\u05D5\u05E8",
    searchPlans: "\u05D7\u05D9\u05E4\u05D5\u05E9 \u05EA\u05D5\u05DB\u05E0\u05D9\u05D5\u05EA...",
    noPlans: "\u05D0\u05D9\u05DF \u05EA\u05D5\u05DB\u05E0\u05D9\u05D5\u05EA \u05E2\u05D3\u05D9\u05D9\u05DF",
    noPlansDescription: "\u05E6\u05D5\u05E8 \u05EA\u05D5\u05DB\u05E0\u05D9\u05EA \u05E7\u05E8\u05D1 \u05D7\u05D3\u05E9\u05D4 \u05DB\u05D3\u05D9 \u05DC\u05D4\u05EA\u05D7\u05D9\u05DC",
    planNotFound: "\u05D4\u05EA\u05D5\u05DB\u05E0\u05D9\u05EA \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D4",
    backToPlans: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05EA\u05D5\u05DB\u05E0\u05D9\u05D5\u05EA",
    backToList: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E8\u05E9\u05D9\u05DE\u05D4",
    deletePlan: "\u05DE\u05D7\u05E7",
    confirmDelete: "\u05D4\u05D0\u05DD \u05D0\u05EA\u05D4 \u05D1\u05D8\u05D5\u05D7 \u05E9\u05D1\u05E8\u05E6\u05D5\u05E0\u05DA \u05DC\u05DE\u05D7\u05D5\u05E7 \u05D0\u05EA \u05D4\u05EA\u05D5\u05DB\u05E0\u05D9\u05EA?",
    createdBy: "\u05E0\u05D5\u05E6\u05E8 \u05E2\u05F4\u05D9",
    progress: "\u05D4\u05EA\u05E7\u05D3\u05DE\u05D5\u05EA",
    progressLabel: "{completed} \u05DE\u05EA\u05D5\u05DA {total} \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA \u05D4\u05D5\u05E9\u05DC\u05DE\u05D5",
    noParticipants: "\u05D0\u05D9\u05DF \u05DE\u05E9\u05EA\u05EA\u05E4\u05D9\u05DD \u05E2\u05D3\u05D9\u05D9\u05DF",
    addParticipant: "\u05D4\u05D5\u05E1\u05E3 \u05DE\u05E9\u05EA\u05EA\u05E3",
    participantName: "\u05E9\u05DD",
    participantEmail: "\u05D0\u05D9\u05DE\u05D9\u05D9\u05DC (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)",
    participantRole: "\u05EA\u05E4\u05E7\u05D9\u05D3 (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)",
    addSubject: "\u05D4\u05D5\u05E1\u05E3 \u05E0\u05D5\u05E9\u05D0",
    subjectTitle: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05D4\u05E0\u05D5\u05E9\u05D0",
    noSubjects: "\u05D0\u05D9\u05DF \u05E0\u05D5\u05E9\u05D0\u05D9\u05DD \u05E2\u05D3\u05D9\u05D9\u05DF",
    noSubjectsDescription: "\u05D4\u05D5\u05E1\u05E3 \u05E0\u05D5\u05E9\u05D0 \u05DB\u05D3\u05D9 \u05DC\u05D4\u05EA\u05D7\u05D9\u05DC \u05DC\u05E0\u05D4\u05DC \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA",
    addTask: "\u05DE\u05E9\u05D9\u05DE\u05D4",
    taskDescription: "\u05EA\u05D9\u05D0\u05D5\u05E8 \u05D4\u05DE\u05E9\u05D9\u05DE\u05D4",
    assignee: "\u05D0\u05D7\u05E8\u05D0\u05D9",
    noTasksInSubject: "\u05D0\u05D9\u05DF \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA \u05D1\u05E0\u05D5\u05E9\u05D0 \u05D6\u05D4",
    print: "\u05D4\u05D3\u05E4\u05E1",
    printPlan: "\u05D4\u05D3\u05E4\u05E1 \u05EA\u05D5\u05DB\u05E0\u05D9\u05EA",
    loading: "\u05D8\u05D5\u05E2\u05DF \u05EA\u05D5\u05DB\u05E0\u05D9\u05D5\u05EA...",
    loadingPlan: "\u05D8\u05D5\u05E2\u05DF \u05EA\u05D5\u05DB\u05E0\u05D9\u05EA...",
    calendar: "\u05DC\u05D5\u05D7 \u05E9\u05E0\u05D4",
    addDepartment: "\u05D4\u05D5\u05E1\u05E3 \u05DE\u05D7\u05DC\u05E7\u05D4",
    addMeasure: "\u05D4\u05D5\u05E1\u05E3 \u05DE\u05D3\u05D3",
    branchName: "\u05E9\u05DD \u05E1\u05E0\u05D9\u05E3",
    collect: "\u05D0\u05E1\u05D5\u05E3",
    companies: "\u05D7\u05D1\u05E8\u05D5\u05EA",
    createFirst: "\u05E6\u05D5\u05E8 \u05D0\u05EA \u05D4\u05EA\u05D5\u05DB\u05E0\u05D9\u05EA \u05D4\u05E8\u05D0\u05E9\u05D5\u05E0\u05D4 \u05E9\u05DC\u05DA \u05DB\u05D3\u05D9 \u05DC\u05D4\u05EA\u05D7\u05D9\u05DC",
    dashboards: "\u05DC\u05D5\u05D7\u05D5\u05EA \u05D1\u05E7\u05E8\u05D4",
    dateRange: "\u05D8\u05D5\u05D5\u05D7 \u05EA\u05D0\u05E8\u05D9\u05DB\u05D9\u05DD",
    define: "\u05D4\u05D2\u05D3\u05E8",
    deleteSalesReport: "\u05DE\u05D7\u05E7 \u05D3\u05D5\u05D7 \u05DE\u05DB\u05D9\u05E8\u05D5\u05EA",
    departmentName: "\u05E9\u05DD \u05DE\u05D7\u05DC\u05E7\u05D4",
    departments: "\u05DE\u05D7\u05DC\u05E7\u05D5\u05EA",
    employees: "\u05E2\u05D5\u05D1\u05D3\u05D9\u05DD",
    employeesDescription: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05E2\u05D5\u05D1\u05D3\u05D9 \u05D4\u05D7\u05D1\u05E8\u05D4",
    explore: "\u05D7\u05E7\u05D5\u05E8",
    hats: "\u05DB\u05D5\u05D1\u05E2\u05D9\u05DD",
    hatsDescription: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05DB\u05D5\u05D1\u05E2\u05D9\u05DD \u05D5\u05EA\u05E4\u05E7\u05D9\u05D3\u05D9\u05DD \u05D1\u05D0\u05E8\u05D2\u05D5\u05DF",
    intervalType: "\u05E1\u05D5\u05D2 \u05DE\u05E8\u05D5\u05D5\u05D7",
    manageCompanies: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05D7\u05D1\u05E8\u05D5\u05EA",
    measureName: "\u05E9\u05DD \u05DE\u05D3\u05D3",
    measures: "\u05DE\u05D3\u05D3\u05D9\u05DD",
    metrics: "\u05DE\u05D3\u05D3\u05D9\u05DD",
    metricsDescription: "\u05DE\u05E2\u05E7\u05D1 \u05D5\u05E0\u05D9\u05D4\u05D5\u05DC \u05DE\u05D3\u05D3\u05D9 \u05D4\u05D7\u05D1\u05E8\u05D4",
    newSalesReport: "\u05D3\u05D5\u05D7 \u05DE\u05DB\u05D9\u05E8\u05D5\u05EA \u05D7\u05D3\u05E9",
    noDashboards: "\u05D0\u05D9\u05DF \u05DC\u05D5\u05D7\u05D5\u05EA \u05D1\u05E7\u05E8\u05D4 \u05E2\u05D3\u05D9\u05D9\u05DF",
    noData: "\u05D0\u05D9\u05DF \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD",
    noSalesReports: "\u05D0\u05D9\u05DF \u05D3\u05D5\u05D7\u05D5\u05EA \u05DE\u05DB\u05D9\u05E8\u05D5\u05EA \u05E2\u05D3\u05D9\u05D9\u05DF",
    noWidgets: "\u05DC\u05D0 \u05D4\u05D5\u05D2\u05D3\u05E8\u05D5 \u05D5\u05D5\u05D9\u05D3\u05D2\u05F3\u05D8\u05D9\u05DD",
    povs: "\u05E0\u05E7\u05D5\u05D3\u05D5\u05EA \u05DE\u05D1\u05D8",
    povsDescription: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05E0\u05E7\u05D5\u05D3\u05D5\u05EA \u05DE\u05D1\u05D8",
    salesReport: "\u05D3\u05D5\u05D7 \u05DE\u05DB\u05D9\u05E8\u05D5\u05EA",
    spreadType: "\u05E1\u05D5\u05D2 \u05E4\u05E8\u05D9\u05E1\u05D4",
    updateSalesReport: "\u05E2\u05D3\u05DB\u05DF \u05D3\u05D5\u05D7 \u05DE\u05DB\u05D9\u05E8\u05D5\u05EA",
    widgetName: "\u05E9\u05DD \u05D5\u05D5\u05D9\u05D3\u05D2\u05F3\u05D8",
    widgetPlaceholder: "\u05D1\u05D7\u05E8 \u05D5\u05D5\u05D9\u05D3\u05D2\u05F3\u05D8...",
    weekdays: {
      sunday: "\u05E8\u05D0\u05E9\u05D5\u05DF",
      monday: "\u05E9\u05E0\u05D9",
      tuesday: "\u05E9\u05DC\u05D9\u05E9\u05D9",
      wednesday: "\u05E8\u05D1\u05D9\u05E2\u05D9",
      thursday: "\u05D7\u05DE\u05D9\u05E9\u05D9",
      friday: "\u05E9\u05D9\u05E9\u05D9",
      saturday: "\u05E9\u05D1\u05EA"
    },
    calendarTitle: "\u05DC\u05D5\u05D7 \u05E9\u05E0\u05D4",
    today: "\u05D4\u05D9\u05D5\u05DD",
    activePlan: "\u05EA\u05D5\u05DB\u05E0\u05D9\u05EA \u05E4\u05E2\u05D9\u05DC\u05D4",
    completed: "\u05D4\u05D5\u05E9\u05DC\u05DD",
    highPriorityTask: "\u05DE\u05E9\u05D9\u05DE\u05D4 \u05D1\u05E2\u05D3\u05D9\u05E4\u05D5\u05EA \u05D2\u05D1\u05D5\u05D4\u05D4",
    mediumPriorityTask: "\u05DE\u05E9\u05D9\u05DE\u05D4 \u05D1\u05E2\u05D3\u05D9\u05E4\u05D5\u05EA \u05D1\u05D9\u05E0\u05D5\u05E0\u05D9\u05EA",
    more: "\u05E0\u05D5\u05E1\u05E4\u05D9\u05DD",
    actionItemsTitle: "\u05DE\u05E9\u05D9\u05DE\u05D5\u05EA",
    totalTasks: '\u05E1\u05D4"\u05DB \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA',
    pending: "\u05DE\u05DE\u05EA\u05D9\u05E0\u05D5\u05EA",
    overdue: "\u05D1\u05D0\u05D9\u05D7\u05D5\u05E8",
    searchTasks: "\u05D7\u05D9\u05E4\u05D5\u05E9 \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA...",
    filterAll: "\u05D4\u05DB\u05DC",
    filterPending: "\u05DE\u05DE\u05EA\u05D9\u05E0\u05D5\u05EA",
    filterCompleted: "\u05D4\u05D5\u05E9\u05DC\u05DE\u05D5",
    filterOverdue: "\u05D1\u05D0\u05D9\u05D7\u05D5\u05E8",
    filterToday: "\u05D4\u05D9\u05D5\u05DD",
    filterWeek: "\u05D4\u05E9\u05D1\u05D5\u05E2",
    sortByDate: "\u05DE\u05D9\u05D9\u05DF \u05DC\u05E4\u05D9 \u05EA\u05D0\u05E8\u05D9\u05DA",
    sortByPriority: "\u05DE\u05D9\u05D9\u05DF \u05DC\u05E4\u05D9 \u05E2\u05D3\u05D9\u05E4\u05D5\u05EA",
    sortByPlan: "\u05DE\u05D9\u05D9\u05DF \u05DC\u05E4\u05D9 \u05EA\u05D5\u05DB\u05E0\u05D9\u05EA",
    noPendingTasks: "\u05D0\u05D9\u05DF \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA \u05DE\u05DE\u05EA\u05D9\u05E0\u05D5\u05EA",
    noCompletedTasks: "\u05D0\u05D9\u05DF \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA \u05E9\u05D4\u05D5\u05E9\u05DC\u05DE\u05D5",
    noOverdueTasks: "\u05D0\u05D9\u05DF \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA \u05D1\u05D0\u05D9\u05D7\u05D5\u05E8",
    noTasks: "\u05D0\u05D9\u05DF \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA",
    priorityHigh: "\u05D2\u05D1\u05D5\u05D4",
    priorityMedium: "\u05D1\u05D9\u05E0\u05D5\u05E0\u05D9",
    priorityLow: "\u05E0\u05DE\u05D5\u05DA",
    manageCompany: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05D7\u05D1\u05E8\u05D4",
    reports: "\u05D3\u05D5\u05D7\u05D5\u05EA",
    visualizations: "\u05EA\u05E6\u05D5\u05D2\u05D5\u05EA",
    actionStates: "\u05DE\u05E6\u05D1\u05D9 \u05DE\u05E9\u05D9\u05DE\u05D5\u05EA",
    alerts: "\u05D4\u05EA\u05E8\u05D0\u05D5\u05EA",
    bizpot: {
      title: "\u05DE\u05D1\u05D3\u05E7 \u05E4\u05D5\u05D8\u05E0\u05E6\u05D9\u05D0\u05DC \u05E2\u05E1\u05E7\u05D9",
      brand: "TurnKeyOS.AI",
      description: "\u05DE\u05D8\u05E8\u05EA \u05D4\u05DE\u05D1\u05D3\u05E7 \u05D4\u05D9\u05D0 \u05DC\u05E8\u05D0\u05D5\u05EA \u05DE\u05D4\u05DD \u05D4\u05E6\u05D3\u05D3\u05D9\u05DD \u05D4\u05D7\u05DC\u05E9\u05D9\u05DD \u05D5\u05D4\u05E0\u05E7\u05D5\u05D3\u05D5\u05EA \u05D4\u05D1\u05E2\u05D9\u05D9\u05EA\u05D9\u05D5\u05EA \u05D1\u05E2\u05E1\u05E7 \u05E9\u05DC\u05DA, \u05DB\u05D3\u05D9 \u05E9\u05E0\u05D5\u05DB\u05DC \u05DC\u05D4\u05E6\u05D9\u05E2 \u05DC\u05DA \u05EA\u05DB\u05E0\u05D9\u05EA \u05E2\u05E1\u05E7\u05D9\u05EA \u05DC\u05E9\u05D9\u05E4\u05D5\u05E8 \u05D4\u05D4\u05DB\u05E0\u05E1\u05D5\u05EA \u05D5\u05D4\u05D2\u05D1\u05E8\u05EA \u05D4\u05D9\u05E2\u05D9\u05DC\u05D5\u05EA \u05D1\u05E2\u05E1\u05E7.",
      important: "\u05D7\u05E9\u05D5\u05D1: \u05D0\u05E0\u05D0 \u05D4\u05E7\u05E4\u05D9\u05D3\u05D5 \u05DC\u05DE\u05DC\u05D0 \u05D0\u05EA \u05DB\u05DC \u05D4\u05E9\u05D0\u05DC\u05D5\u05EA, \u05D2\u05DD \u05DB\u05D0\u05DC\u05D4 \u05E9\u05E2\u05E9\u05D5\u05D9\u05D5\u05EA \u05DC\u05D4\u05E8\u05D0\u05D5\u05EA \u05DC\u05D0 \u05E8\u05DC\u05D5\u05D5\u05E0\u05D8\u05D9\u05D5\u05EA.",
      details: "\u05E4\u05E8\u05D8\u05D9\u05DD",
      firstName: "\u05E9\u05DD \u05E4\u05E8\u05D8\u05D9",
      lastName: "\u05E9\u05DD \u05DE\u05E9\u05E4\u05D7\u05D4",
      title2: "\u05EA\u05E4\u05E7\u05D9\u05D3",
      company: "\u05D7\u05D1\u05E8\u05D4",
      businessType: "\u05E1\u05D5\u05D2 \u05D4\u05D7\u05D1\u05E8\u05D4",
      employeeCount: "\u05DE\u05E1\u05E4\u05E8 \u05E2\u05D5\u05D1\u05D3\u05D9\u05DD",
      email: "\u05D3\u05D5\u05D0\u05DC",
      phone: "\u05D8\u05DC\u05E4\u05D5\u05DF",
      startAssessment: "\u05D4\u05EA\u05D7\u05DC \u05DE\u05D1\u05D3\u05E7",
      instructions: "\u05D4\u05D5\u05E8\u05D0\u05D5\u05EA \u05DC\u05DE\u05D9\u05DC\u05D5\u05D9 \u05E9\u05D0\u05DC\u05D5\u05DF \u05D4\u05E0\u05D9\u05EA\u05D5\u05D7 \u05D4\u05E2\u05E1\u05E7\u05D9",
      questionCount: "\u05D9\u05E9 {count} \u05E9\u05D0\u05DC\u05D5\u05EA \u05DE\u05DE\u05D5\u05E1\u05E4\u05E8\u05D5\u05EA. \u05E1\u05DE\u05DF \u05D0\u05EA \u05EA\u05E9\u05D5\u05D1\u05EA\u05DA \u05DC\u05DB\u05DC \u05D0\u05D7\u05EA \u05DE\u05D4\u05DF.",
      answerPlus: '+ (\u05E4\u05DC\u05D5\u05E1) = "\u05D0\u05E0\u05D9 \u05DE\u05E1\u05DB\u05D9\u05DD"',
      answerQuestion: '? (\u05E1\u05D9\u05DE\u05DF \u05E9\u05D0\u05DC\u05D4) = "\u05DC\u05D0 \u05D1\u05D8\u05D5\u05D7"',
      answerMinus: '\u2013 (\u05DE\u05D9\u05E0\u05D5\u05E1) = "\u05D0\u05D9\u05E0\u05D9 \u05DE\u05E1\u05DB\u05D9\u05DD"',
      fontSize: "\u05D2\u05D5\u05D3\u05DC \u05D2\u05D5\u05E4\u05DF",
      finish: "\u05E1\u05D9\u05D5\u05DD",
      unansweredWarning: "\u05D9\u05E9 \u05DC\u05DE\u05DC\u05D0 \u05D0\u05EA \u05DB\u05DC \u05D4\u05E9\u05D0\u05DC\u05D5\u05EA \u05DC\u05E4\u05E0\u05D9 \u05DC\u05D7\u05D9\u05E6\u05D4 \u05E2\u05DC \u05E1\u05D9\u05D5\u05DD ({count} \u05D7\u05E1\u05E8\u05D5\u05EA)",
      confirmDetails: "\u05D0\u05D9\u05E9\u05D5\u05E8 \u05E4\u05E8\u05D8\u05D9\u05DD",
      submit: "\u05E9\u05DC\u05D9\u05D7\u05D4",
      successMessage: "\u05D4\u05D8\u05D5\u05E4\u05E1 \u05E0\u05E9\u05DC\u05D7 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4"
    },
    ppa: {
      title: "\u05DE\u05D1\u05D3\u05E7 \u05E4\u05D5\u05D8\u05E0\u05E6\u05D9\u05D0\u05DC \u05D0\u05D9\u05E9\u05D9",
      brand: "TurnKeyOS.AI",
      details: "\u05E4\u05E8\u05D8\u05D9\u05DD",
      firstName: "\u05E9\u05DD \u05E4\u05E8\u05D8\u05D9",
      lastName: "\u05E9\u05DD \u05DE\u05E9\u05E4\u05D7\u05D4",
      company: "\u05D7\u05D1\u05E8\u05D4",
      title2: "\u05EA\u05E4\u05E7\u05D9\u05D3",
      email: "\u05D3\u05D5\u05D0\u05F4\u05DC",
      phone: "\u05D8\u05DC\u05E4\u05D5\u05DF",
      startAssessment: "\u05D4\u05EA\u05D7\u05DC \u05DE\u05D1\u05D3\u05E7",
      instructionsTitle: "\u05D0\u05DC \u05EA\u05D9\u05D2\u05E9 \u05D0\u05DC \u05D4\u05E9\u05D0\u05DC\u05D5\u05DF \u05D1\u05D8\u05E8\u05DD \u05E7\u05E8\u05D0\u05EA \u05D0\u05EA \u05D4\u05D4\u05D5\u05E8\u05D0\u05D5\u05EA",
      instruction1: "\u05E9\u05D0\u05EA\u05D4 \u05DE\u05D1\u05D9\u05DF \u05DB\u05DC \u05E9\u05D0\u05DC\u05D4: \u05E7\u05E8\u05D0 \u05D0\u05D5\u05EA\u05D4 \u05DB\u05DE\u05E1\u05E4\u05E8 \u05D4\u05E4\u05E2\u05DE\u05D9\u05DD \u05D4\u05E0\u05D7\u05D5\u05E5. \u05E2\u05E0\u05D4 \u05D1\u05D1\u05E7\u05E9\u05D4 \u05E2\u05DC \u05DB\u05DC \u05E9\u05D0\u05DC\u05D4.",
      instruction2: "\u05D0\u05DC \u05EA\u05EA\u05E2\u05DB\u05D1 \u05E2\u05DC \u05E9\u05D0\u05DC\u05D4 \u05D0\u05D7\u05EA \u05D6\u05DE\u05DF \u05E8\u05D1 \u05DE\u05D3\u05D9. \u05E2\u05E0\u05D4 \u05E2\u05DC\u05D9\u05D4 \u05D1\u05E8\u05D2\u05E2 \u05E9\u05D0\u05EA\u05D4 \u05DE\u05D1\u05D9\u05DF \u05D0\u05D5\u05EA\u05D4 \u05D5\u05E2\u05D1\u05D5\u05E8 \u05DC\u05E9\u05D0\u05DC\u05D4 \u05D4\u05D1\u05D0\u05D4.",
      instruction3: "\u05DB\u05D0\u05E9\u05E8 \u05D4\u05EA\u05E9\u05D5\u05D1\u05D4 \u05EA\u05D4\u05D9\u05D4 \u05E9\u05D5\u05E0\u05D4 \u05D0\u05DD \u05D0\u05EA\u05D4 \u05DE\u05EA\u05D9\u05D9\u05D7\u05E1 \u05DC\u05E2\u05D1\u05E8 \u05DC\u05E2\u05D5\u05DE\u05EA \u05D4\u05D4\u05D5\u05D5\u05D4, \u05E2\u05E0\u05D4 \u05DB\u05D1\u05D4\u05D5\u05D5\u05D4.",
      instruction4plus: '+ (\u05E4\u05DC\u05D5\u05E1) \u05E4\u05D9\u05E8\u05D5\u05E9\u05D5 "\u05D0\u05E0\u05D9 \u05DE\u05E1\u05DB\u05D9\u05DD", "\u05DB\u05DF" \u05D0\u05D5 "\u05D1\u05D3\u05E8\u05DA-\u05DB\u05DC\u05DC \u05DB\u05DF"',
      instruction4question: '? (\u05E1\u05D9\u05DE\u05DF \u05E9\u05D0\u05DC\u05D4) \u05E4\u05D9\u05E8\u05D5\u05E9\u05D5 "\u05DC\u05D0 \u05D1\u05D8\u05D5\u05D7", "\u05D0\u05D5\u05DC\u05D9"',
      instruction4minus: '\u2013 (\u05DE\u05D9\u05E0\u05D5\u05E1) \u05E4\u05D9\u05E8\u05D5\u05E9\u05D5 "\u05D0\u05D9\u05E0\u05D9 \u05DE\u05E1\u05DB\u05D9\u05DD", "\u05DC\u05D0" \u05D0\u05D5 "\u05D1\u05D3\u05E8\u05DA-\u05DB\u05DC\u05DC \u05DC\u05D0"',
      fontSize: "\u05D2\u05D5\u05D3\u05DC \u05D2\u05D5\u05E4\u05DF",
      submitForm: "\u05E9\u05D2\u05E8 \u05D8\u05D5\u05E4\u05E1",
      confirmDetails: "\u05D0\u05D9\u05E9\u05D5\u05E8 \u05E4\u05E8\u05D8\u05D9\u05DD",
      disclaimer: "\u05D0\u05E0\u05D9 \u05DE\u05D0\u05E9\u05E8 \u05E9\u05D0\u05E0\u05D9 \u05D4\u05E8\u05E9\u05D5\u05DD/\u05D4 \u05DC\u05DE\u05E2\u05DC\u05D4, \u05DE\u05D9\u05DC\u05D0\u05EA\u05D9 \u05D0\u05EA \u05D4\u05EA\u05E9\u05D5\u05D1\u05D5\u05EA \u05D1\u05E2\u05E6\u05DE\u05D9 \u05D5\u05DC\u05DC\u05D0 \u05E1\u05D9\u05D5\u05E2 \u05DE\u05E6\u05D3 \u05D0\u05D3\u05DD \u05D0\u05D7\u05E8",
      confirm: "\u05D0\u05D9\u05E9\u05D5\u05E8",
      successMessage: "\u05D4\u05D8\u05D5\u05E4\u05E1 \u05E0\u05E9\u05DC\u05D7 \u05D1\u05D4\u05E6\u05DC\u05D7\u05D4"
    }
  },
  admin: {
    dashboard: "\u05DC\u05D5\u05D7 \u05D1\u05E7\u05E8\u05D4 \u05E0\u05D9\u05D4\u05D5\u05DC\u05D9",
    platformOverview: "\u05E1\u05E7\u05D9\u05E8\u05EA \u05D4\u05E4\u05DC\u05D8\u05E4\u05D5\u05E8\u05DE\u05D4 \u05D5\u05E0\u05D9\u05D4\u05D5\u05DC",
    totalUsers: "\u05E1\u05D4\u05F4\u05DB \u05DE\u05E9\u05EA\u05DE\u05E9\u05D9\u05DD",
    organizations: "\u05D0\u05E8\u05D2\u05D5\u05E0\u05D9\u05DD",
    activeAssessments: "\u05D4\u05E2\u05E8\u05DB\u05D5\u05EA \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA",
    completedSessions: "\u05E1\u05E9\u05E0\u05D9\u05DD \u05E9\u05D4\u05D5\u05E9\u05DC\u05DE\u05D5",
    weeklyActivity: "\u05E4\u05E2\u05D9\u05DC\u05D5\u05EA \u05E9\u05D1\u05D5\u05E2\u05D9\u05EA",
    topOrganizations: "\u05D0\u05E8\u05D2\u05D5\u05E0\u05D9\u05DD \u05DE\u05D5\u05D1\u05D9\u05DC\u05D9\u05DD",
    quickActions: "\u05E4\u05E2\u05D5\u05DC\u05D5\u05EA \u05DE\u05D4\u05D9\u05E8\u05D5\u05EA",
    manageUsers: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05DE\u05E9\u05EA\u05DE\u05E9\u05D9\u05DD",
    manageOrganizations: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05D0\u05E8\u05D2\u05D5\u05E0\u05D9\u05DD",
    createAssessment: "\u05E6\u05D5\u05E8 \u05D4\u05E2\u05E8\u05DB\u05D4",
    systemSettings: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05DE\u05E2\u05E8\u05DB\u05EA",
    sessionsStarted: "\u05E1\u05E9\u05E0\u05D9\u05DD \u05E9\u05D4\u05EA\u05D7\u05D9\u05DC\u05D5",
    completions: "\u05D4\u05E9\u05DC\u05DE\u05D5\u05EA"
  },
  sessions: {
    title: "\u05E1\u05E9\u05E0\u05D9 \u05D4\u05E2\u05E8\u05DB\u05D4",
    completed: "\u05D4\u05D5\u05E9\u05DC\u05DE\u05D5",
    inProgress: "\u05D1\u05EA\u05D4\u05DC\u05D9\u05DA",
    abandoned: "\u05E0\u05E0\u05D8\u05E9\u05D5",
    viewResults: "\u05E6\u05E4\u05D4 \u05D1\u05EA\u05D5\u05E6\u05D0\u05D5\u05EA",
    exportCsv: "\u05D9\u05D9\u05E6\u05D5\u05D0 CSV",
    compare: "\u05D4\u05E9\u05D5\u05D5\u05D4",
    participant: "\u05DE\u05E9\u05EA\u05EA\u05E3",
    organization: "\u05D0\u05E8\u05D2\u05D5\u05DF",
    assessment: "\u05D4\u05E2\u05E8\u05DB\u05D4",
    progress: "\u05D4\u05EA\u05E7\u05D3\u05DE\u05D5\u05EA",
    status: "\u05E1\u05D8\u05D8\u05D5\u05E1",
    date: "\u05EA\u05D0\u05E8\u05D9\u05DA",
    noCompleted: "\u05D0\u05D9\u05DF \u05E1\u05E9\u05E0\u05D9\u05DD \u05E9\u05D4\u05D5\u05E9\u05DC\u05DE\u05D5",
    noInProgress: "\u05D0\u05D9\u05DF \u05E1\u05E9\u05E0\u05D9\u05DD \u05D1\u05EA\u05D4\u05DC\u05D9\u05DA",
    resultsTitle: "\u05EA\u05D5\u05E6\u05D0\u05D5\u05EA \u05D4\u05D4\u05E2\u05E8\u05DB\u05D4",
    participantInfo: "\u05E4\u05E8\u05D8\u05D9 \u05D4\u05DE\u05E9\u05EA\u05EA\u05E3",
    scoreSummary: "\u05E1\u05D9\u05DB\u05D5\u05DD \u05E6\u05D9\u05D5\u05E0\u05D9\u05DD",
    overallScore: "\u05E6\u05D9\u05D5\u05DF \u05DB\u05DC\u05DC\u05D9",
    business: "\u05E2\u05E1\u05E7\u05D9",
    personal: "\u05D0\u05D9\u05E9\u05D9",
    visualization: "\u05EA\u05E6\u05D5\u05D2\u05EA \u05D4\u05EA\u05D5\u05E6\u05D0\u05D5\u05EA",
    progressBars: "\u05E4\u05E1\u05D9 \u05D4\u05EA\u05E7\u05D3\u05DE\u05D5\u05EA",
    radarChart: "\u05EA\u05E8\u05E9\u05D9\u05DD \u05DE\u05DB\u05F4\u05DE",
    gauges: "\u05DE\u05D3\u05D3\u05D9\u05DD",
    categoryAnalysis: "\u05E0\u05D9\u05EA\u05D5\u05D7 \u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA",
    excellent: "\u05DE\u05E6\u05D5\u05D9\u05DF",
    good: "\u05D8\u05D5\u05D1",
    fair: "\u05E1\u05D1\u05D9\u05E8",
    needsWork: "\u05D3\u05D5\u05E8\u05E9 \u05E2\u05D1\u05D5\u05D3\u05D4",
    backToSessions: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E1\u05E9\u05E0\u05D9\u05DD",
    printResults: "\u05D4\u05D3\u05E4\u05E1 \u05EA\u05D5\u05E6\u05D0\u05D5\u05EA",
    downloadPdf: "\u05D4\u05D5\u05E8\u05D3 PDF"
  },
  orgboard: {
    title: "\u05DC\u05D5\u05D7 \u05D0\u05E8\u05D2\u05D5\u05DF",
    employees: "\u05E2\u05D5\u05D1\u05D3\u05D9\u05DD",
    divisions: "\u05D0\u05D2\u05E4\u05D9\u05DD",
    departments: "\u05DE\u05D7\u05DC\u05E7\u05D5\u05EA",
    sections: "\u05D9\u05D7\u05D9\u05D3\u05D5\u05EA",
    functions: "\u05EA\u05E4\u05E7\u05D9\u05D3\u05D9\u05DD",
    addEmployee: "\u05D4\u05D5\u05E1\u05E3 \u05E2\u05D5\u05D1\u05D3",
    addDivision: "\u05D4\u05D5\u05E1\u05E3 \u05D0\u05D2\u05E3",
    addDepartment: "\u05D4\u05D5\u05E1\u05E3 \u05DE\u05D7\u05DC\u05E7\u05D4",
    addSection: "\u05D4\u05D5\u05E1\u05E3 \u05D9\u05D7\u05D9\u05D3\u05D4",
    addFunction: "\u05D4\u05D5\u05E1\u05E3 \u05EA\u05E4\u05E7\u05D9\u05D3",
    editMode: "\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4",
    viewMode: "\u05DE\u05E6\u05D1 \u05E6\u05E4\u05D9\u05D9\u05D4",
    exportPng: "\u05D9\u05D9\u05E6\u05D5\u05D0 PNG",
    exportSvg: "\u05D9\u05D9\u05E6\u05D5\u05D0 SVG",
    exportJson: "\u05D9\u05D9\u05E6\u05D5\u05D0 JSON",
    importJson: "\u05D9\u05D9\u05D1\u05D5\u05D0 JSON",
    print: "\u05D4\u05D3\u05E4\u05E1\u05D4",
    companySetup: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05D7\u05D1\u05E8\u05D4",
    vfp: "\u05EA\u05D5\u05E6\u05E8 \u05E1\u05D5\u05E4\u05D9 \u05D1\u05E2\u05DC \u05E2\u05E8\u05DA",
    manager: "\u05DE\u05E0\u05D4\u05DC",
    noManager: "\u05DC\u05DC\u05D0 \u05DE\u05E0\u05D4\u05DC",
    selectManager: "\u05D1\u05D7\u05E8 \u05DE\u05E0\u05D4\u05DC"
  },
  controls: {
    zoom: "\u05D6\u05D5\u05DD",
    textSize: "\u05D2\u05D5\u05D3\u05DC \u05D8\u05E7\u05E1\u05D8",
    divisionWidth: "\u05E8\u05D5\u05D7\u05D1 \u05D0\u05D2\u05E3",
    fitToScreen: "\u05D4\u05EA\u05D0\u05DD \u05DC\u05DE\u05E1\u05DA",
    layout: "\u05E4\u05E8\u05D9\u05E1\u05D4",
    editMode: "\u05DE\u05E6\u05D1 \u05E2\u05E8\u05D9\u05DB\u05D4",
    viewerNoEdit: "\u05E6\u05E4\u05D9\u05D9\u05D4 (\u05DC\u05DC\u05D0 \u05E2\u05E8\u05D9\u05DB\u05D4)",
    showSections: "\u05D4\u05E6\u05D2 \u05D9\u05D7\u05D9\u05D3\u05D5\u05EA",
    showFunctions: "\u05D4\u05E6\u05D2 \u05EA\u05E4\u05E7\u05D9\u05D3\u05D9\u05DD",
    showDetails: "\u05D4\u05E6\u05D2 \u05E4\u05E8\u05D8\u05D9\u05DD",
    companySetup: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05D7\u05D1\u05E8\u05D4",
    filterByDivision: "\u05E1\u05E0\u05DF \u05DC\u05E4\u05D9 \u05D0\u05D2\u05E3",
    filterByDepartment: "\u05E1\u05E0\u05DF \u05DC\u05E4\u05D9 \u05DE\u05D7\u05DC\u05E7\u05D4",
    allDivisions: "\u05DB\u05DC \u05D4\u05D0\u05D2\u05E4\u05D9\u05DD",
    allDepartments: "\u05DB\u05DC \u05D4\u05DE\u05D7\u05DC\u05E7\u05D5\u05EA"
  },
  nav: {
    employees: "\u05E2\u05D5\u05D1\u05D3\u05D9\u05DD",
    admin: "\u05E0\u05D9\u05D4\u05D5\u05DC",
    logout: "\u05D4\u05EA\u05E0\u05EA\u05E7",
    dashboard: "\u05DC\u05D5\u05D7 \u05D1\u05E7\u05E8\u05D4",
    projects: "\u05E4\u05E8\u05D5\u05D9\u05E7\u05D8\u05D9\u05DD",
    settings: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA",
    data: "\u05E0\u05EA\u05D5\u05E0\u05D9\u05DD",
    charts: "\u05D2\u05E8\u05E4\u05D9\u05DD"
  },
  categories: {
    management: "\u05E0\u05D9\u05D4\u05D5\u05DC",
    organization: "\u05D0\u05E8\u05D2\u05D5\u05DF",
    repeatSales: "\u05DE\u05DB\u05D9\u05E8\u05D5\u05EA \u05D7\u05D5\u05D6\u05E8\u05D5\u05EA",
    finance: "\u05DB\u05E1\u05E4\u05D9\u05DD",
    operations: "\u05EA\u05E4\u05E2\u05D5\u05DC",
    quality: "\u05D1\u05E7\u05E8\u05EA \u05D0\u05D9\u05DB\u05D5\u05EA",
    newSales: "\u05DE\u05DB\u05D9\u05E8\u05D5\u05EA \u05D7\u05D3\u05E9\u05D5\u05EA",
    stress: "\u05DE\u05EA\u05D7",
    principles: "\u05E2\u05E7\u05E8\u05D5\u05E0\u05D5\u05EA",
    help: "\u05E7\u05D1\u05DC\u05EA \u05E2\u05D6\u05E8\u05D4"
  },
  errors: {
    generic: "\u05DE\u05E9\u05D4\u05D5 \u05D4\u05E9\u05EA\u05D1\u05E9. \u05D0\u05E0\u05D0 \u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1.",
    network: "\u05E9\u05D2\u05D9\u05D0\u05EA \u05E8\u05E9\u05EA. \u05D0\u05E0\u05D0 \u05D1\u05D3\u05D5\u05E7 \u05D0\u05EA \u05D4\u05D7\u05D9\u05D1\u05D5\u05E8 \u05E9\u05DC\u05DA.",
    unauthorized: "\u05D0\u05D9\u05DF \u05DC\u05DA \u05D4\u05E8\u05E9\u05D0\u05D4 \u05DC\u05D1\u05E6\u05E2 \u05E4\u05E2\u05D5\u05DC\u05D4 \u05D6\u05D5.",
    notFound: "\u05D4\u05DE\u05E9\u05D0\u05D1 \u05D4\u05DE\u05D1\u05D5\u05E7\u05E9 \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0.",
    validation: "\u05D0\u05E0\u05D0 \u05D1\u05D3\u05D5\u05E7 \u05D0\u05EA \u05D4\u05D8\u05D5\u05E4\u05E1 \u05DC\u05E9\u05D2\u05D9\u05D0\u05D5\u05EA.",
    noOrganization: "\u05DC\u05D0 \u05DE\u05E9\u05D5\u05D9\u05DA \u05DC\u05D0\u05E8\u05D2\u05D5\u05DF",
    contactAdmin: "\u05D0\u05E0\u05D0 \u05E4\u05E0\u05D4 \u05DC\u05DE\u05E0\u05D4\u05DC \u05D4\u05DE\u05E2\u05E8\u05DB\u05EA \u05DC\u05E9\u05D9\u05D5\u05DA \u05DC\u05D0\u05E8\u05D2\u05D5\u05DF.",
    loginFailed: "\u05D4\u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA \u05E0\u05DB\u05E9\u05DC\u05D4. \u05D0\u05E0\u05D0 \u05D1\u05D3\u05D5\u05E7 \u05D0\u05EA \u05E4\u05E8\u05D8\u05D9 \u05D4\u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA.",
    sessionFailed: "\u05E0\u05DB\u05E9\u05DC \u05D1\u05D9\u05E6\u05D9\u05E8\u05EA \u05E1\u05E9\u05DF \u05D4\u05E2\u05E8\u05DB\u05D4",
    sessionNotReady: "\u05D4\u05E1\u05E9\u05DF \u05DC\u05D0 \u05DE\u05D5\u05DB\u05DF. \u05D0\u05E0\u05D0 \u05D4\u05DE\u05EA\u05DF \u05E8\u05D2\u05E2.",
    submissionFailed: "\u05E0\u05DB\u05E9\u05DC \u05D1\u05E9\u05DC\u05D9\u05D7\u05EA \u05D4\u05D4\u05E2\u05E8\u05DB\u05D4. \u05D0\u05E0\u05D0 \u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1."
  },
  validation: {
    required: "\u05E9\u05D3\u05D4 \u05D6\u05D4 \u05D4\u05D5\u05D0 \u05D7\u05D5\u05D1\u05D4",
    email: "\u05D0\u05E0\u05D0 \u05D4\u05D6\u05DF \u05DB\u05EA\u05D5\u05D1\u05EA \u05D3\u05D5\u05D0\u05E8 \u05D0\u05DC\u05E7\u05D8\u05E8\u05D5\u05E0\u05D9 \u05EA\u05E7\u05D9\u05E0\u05D4",
    minLength: "\u05D7\u05D9\u05D9\u05D1 \u05DC\u05D4\u05DB\u05D9\u05DC \u05DC\u05E4\u05D7\u05D5\u05EA {min} \u05EA\u05D5\u05D5\u05D9\u05DD",
    maxLength: "\u05D7\u05D9\u05D9\u05D1 \u05DC\u05D4\u05DB\u05D9\u05DC \u05DC\u05DB\u05DC \u05D4\u05D9\u05D5\u05EA\u05E8 {max} \u05EA\u05D5\u05D5\u05D9\u05DD",
    pattern: "\u05E4\u05D5\u05E8\u05DE\u05D8 \u05DC\u05D0 \u05EA\u05E7\u05D9\u05DF"
  },
  settings: {
    budgetSettings: "\u05D4\u05D2\u05D3\u05E8\u05D5\u05EA \u05EA\u05E7\u05E6\u05D9\u05D1",
    budgetName: "\u05E9\u05DD \u05D4\u05EA\u05E7\u05E6\u05D9\u05D1",
    interval: "\u05EA\u05E7\u05D5\u05E4\u05D4",
    period: "\u05D8\u05D5\u05D5\u05D7 \u05EA\u05D0\u05E8\u05D9\u05DB\u05D9\u05DD",
    subjects: "\u05E0\u05D5\u05E9\u05D0\u05D9\u05DD",
    addSubject: "\u05D4\u05D5\u05E1\u05E3 \u05E0\u05D5\u05E9\u05D0",
    addSection: "\u05D4\u05D5\u05E1\u05E3 \u05E1\u05E2\u05D9\u05E3",
    addFirstSection: "\u05D4\u05D5\u05E1\u05E3 \u05E1\u05E2\u05D9\u05E3 \u05E8\u05D0\u05E9\u05D5\u05DF",
    noSubjects: "\u05DC\u05D0 \u05D4\u05D5\u05D2\u05D3\u05E8\u05D5 \u05E0\u05D5\u05E9\u05D0\u05D9\u05DD",
    enterSubjectName: "\u05D4\u05D6\u05DF \u05E9\u05DD \u05E0\u05D5\u05E9\u05D0:",
    enterSectionName: "\u05D4\u05D6\u05DF \u05E9\u05DD \u05E1\u05E2\u05D9\u05E3:",
    enterBudgetName: "\u05D4\u05D6\u05DF \u05E9\u05DD \u05EA\u05E7\u05E6\u05D9\u05D1:",
    isIncome: "\u05D4\u05D0\u05DD \u05D6\u05D5 \u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D9\u05EA \u05D4\u05DB\u05E0\u05E1\u05D4? (\u05D0\u05D9\u05E9\u05D5\u05E8=\u05DB\u05DF, \u05D1\u05D9\u05D8\u05D5\u05DC=\u05DC\u05D0)",
    weekly: "\u05E9\u05D1\u05D5\u05E2\u05D9",
    monthly: "\u05D7\u05D5\u05D3\u05E9\u05D9",
    quarterly: "\u05E8\u05D1\u05E2\u05D5\u05E0\u05D9",
    yearly: "\u05E9\u05E0\u05EA\u05D9",
    general: "\u05DB\u05DC\u05DC\u05D9",
    name: "\u05E9\u05DD",
    intervalType: "\u05E1\u05D5\u05D2 \u05DE\u05E8\u05D5\u05D5\u05D7",
    startDate: "\u05EA\u05D0\u05E8\u05D9\u05DA \u05D4\u05EA\u05D7\u05DC\u05D4",
    endDate: "\u05EA\u05D0\u05E8\u05D9\u05DA \u05E1\u05D9\u05D5\u05DD",
    goals: "\u05D9\u05E2\u05D3\u05D9\u05DD",
    measures: "\u05DE\u05D3\u05D3\u05D9\u05DD",
    credit: "\u05D6\u05DB\u05D5\u05EA",
    debit: "\u05D7\u05D5\u05D1\u05D4",
    calculated: "\u05DE\u05D7\u05D5\u05E9\u05D1",
    currency: "\u05DE\u05D8\u05D1\u05E2",
    updateBudget: "\u05E2\u05D3\u05DB\u05DF \u05EA\u05E7\u05E6\u05D9\u05D1",
    createDataCollection: "\u05E6\u05D5\u05E8 \u05D0\u05D5\u05E1\u05E3 \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD",
    createNewBudget: "\u05EA\u05E7\u05E6\u05D9\u05D1 \u05D7\u05D3\u05E9",
    createBudgetError: "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D9\u05E6\u05D9\u05E8\u05EA \u05EA\u05E7\u05E6\u05D9\u05D1",
    deleteBudget: "\u05DE\u05D7\u05E7 \u05EA\u05E7\u05E6\u05D9\u05D1",
    duplicate: "\u05E9\u05DB\u05E4\u05D5\u05DC",
    confirmDeleteBudget: "\u05D4\u05D0\u05DD \u05D0\u05EA\u05D4 \u05D1\u05D8\u05D5\u05D7 \u05E9\u05D1\u05E8\u05E6\u05D5\u05E0\u05DA \u05DC\u05DE\u05D7\u05D5\u05E7 \u05EA\u05E7\u05E6\u05D9\u05D1 \u05D6\u05D4?"
  },
  actions: {
    create: "\u05E6\u05D5\u05E8",
    cancel: "\u05D1\u05D9\u05D8\u05D5\u05DC",
    delete: "\u05DE\u05D7\u05E7",
    edit: "\u05E2\u05E8\u05D5\u05DA",
    moveUp: "\u05D4\u05D6\u05D6 \u05DC\u05DE\u05E2\u05DC\u05D4",
    moveDown: "\u05D4\u05D6\u05D6 \u05DC\u05DE\u05D8\u05D4",
    confirmDelete: "\u05D4\u05D0\u05DD \u05D0\u05EA\u05D4 \u05D1\u05D8\u05D5\u05D7 \u05E9\u05D1\u05E8\u05E6\u05D5\u05E0\u05DA \u05DC\u05DE\u05D7\u05D5\u05E7 \u05E4\u05E8\u05D9\u05D8 \u05D6\u05D4?"
  }
};
var dictionaries = {
  en: en_default,
  he: he_default
};
var LocaleContext = createContext(null);
function getNestedValue(obj, path) {
  const keys = path.split(".");
  let value = obj;
  for (const key of keys) {
    if (value && typeof value === "object" && key in value) {
      value = value[key];
    } else {
      return void 0;
    }
  }
  return typeof value === "string" ? value : void 0;
}
function replaceParams(str, params) {
  return str.replace(/\{(\w+)\}/g, (_, key) => String(params[key] ?? `{${key}}`));
}
function LocaleProvider({ children, defaultLocale }) {
  const [locale, setLocaleState] = useState(() => {
    const stored = getStorageItem(STORAGE_KEYS.LOCALE, defaultLocale || DEFAULT_LOCALE);
    return stored;
  });
  const setLocale = useCallback((newLocale) => {
    setLocaleState(newLocale);
    setStorageItem(STORAGE_KEYS.LOCALE, newLocale);
    document.documentElement.dir = RTL_LOCALES.includes(newLocale) ? "rtl" : "ltr";
    document.documentElement.lang = newLocale;
  }, []);
  useEffect(() => {
    document.documentElement.dir = RTL_LOCALES.includes(locale) ? "rtl" : "ltr";
    document.documentElement.lang = locale;
  }, [locale]);
  const dictionary = dictionaries[locale];
  const t = useCallback(
    (key, params) => {
      const value2 = getNestedValue(dictionary, key);
      if (!value2) {
        console.warn(`Missing translation for key: ${key}`);
        return key;
      }
      if (params) {
        return replaceParams(value2, params);
      }
      return value2;
    },
    [dictionary]
  );
  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      isRTL: RTL_LOCALES.includes(locale),
      direction: RTL_LOCALES.includes(locale) ? "rtl" : "ltr",
      dictionary
    }),
    [locale, setLocale, t, dictionary]
  );
  return /* @__PURE__ */ jsx(LocaleContext.Provider, { value, children });
}
function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
}
function useTranslation() {
  const { t, locale, isRTL } = useLocale();
  return { t, locale, isRTL };
}
var SubscriptionContext = createContext(null);
function SubscriptionProvider({ children, organizationId }) {
  const subscriptionState = useSubscription(organizationId);
  return /* @__PURE__ */ jsx(SubscriptionContext.Provider, { value: subscriptionState, children });
}
function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscriptionContext must be used within a SubscriptionProvider");
  }
  return context;
}
function TrialBanner() {
  const { isTrialing, trialDaysLeft } = useSubscriptionContext();
  const [dismissed, setDismissed] = useState(false);
  if (!isTrialing || dismissed) {
    return null;
  }
  const isUrgent = trialDaysLeft <= 3;
  const bgColor = isUrgent ? "#ed6c02" : "#0288d1";
  const bannerStyle = {
    position: "sticky",
    top: 0,
    zIndex: 1200,
    width: "100%",
    backgroundColor: bgColor,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 16px",
    fontSize: "14px",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    boxSizing: "border-box"
  };
  const buttonStyle = {
    marginLeft: 12,
    padding: "4px 12px",
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.5)",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    textDecoration: "none"
  };
  const closeStyle = {
    marginLeft: 12,
    background: "none",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    fontSize: "18px",
    lineHeight: 1,
    padding: "2px 6px"
  };
  const message = trialDaysLeft === 1 ? "1 day left in your free trial \u2014 Upgrade now to keep access" : `${trialDaysLeft} days left in your free trial \u2014 Upgrade now`;
  return /* @__PURE__ */ jsxs("div", { style: bannerStyle, children: [
    /* @__PURE__ */ jsx("span", { children: message }),
    /* @__PURE__ */ jsx("a", { href: "/billing", style: buttonStyle, children: "Upgrade now" }),
    /* @__PURE__ */ jsx(
      "button",
      {
        style: closeStyle,
        onClick: () => setDismissed(true),
        "aria-label": "Close",
        children: "\u2715"
      }
    )
  ] });
}
var fontFamily = '"Roboto", "Helvetica", "Arial", sans-serif';
function Paywall({ children, isAdmin = false }) {
  const {
    loading,
    canAccess,
    isTrialing,
    isPastDue,
    isCancelled,
    isExpired,
    subscription
  } = useSubscriptionContext();
  if (isAdmin) {
    return /* @__PURE__ */ jsx(Fragment, { children });
  }
  if (loading) {
    return /* @__PURE__ */ jsx("div", { style: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", fontFamily }, children: /* @__PURE__ */ jsxs("div", { style: { textAlign: "center" }, children: [
      /* @__PURE__ */ jsx("div", { style: { fontSize: 24, marginBottom: 8 }, children: "\u23F3" }),
      /* @__PURE__ */ jsx("p", { style: { color: "#666" }, children: "Loading..." })
    ] }) });
  }
  if (!subscription) {
    return /* @__PURE__ */ jsx(Fragment, { children });
  }
  if (isPastDue) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("div", { style: {
        backgroundColor: "#d32f2f",
        color: "#fff",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        fontFamily,
        fontSize: 14
      }, children: [
        /* @__PURE__ */ jsx("span", { children: "\u26A0\uFE0F Your payment is past due. Please update your payment method to avoid service interruption." }),
        /* @__PURE__ */ jsx(
          "a",
          {
            href: "/billing",
            style: {
              color: "#fff",
              padding: "4px 12px",
              border: "1px solid rgba(255,255,255,0.5)",
              borderRadius: 4,
              textDecoration: "none",
              fontSize: 13,
              fontWeight: 600
            },
            children: "Update payment"
          }
        )
      ] }),
      children
    ] });
  }
  if (canAccess) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      isTrialing && /* @__PURE__ */ jsx(TrialBanner, {}),
      children
    ] });
  }
  return /* @__PURE__ */ jsx(UpgradePage, { status: isCancelled ? "cancelled" : "expired" });
}
function UpgradePage({ status }) {
  const statusMessage = status === "cancelled" ? "Your subscription has been cancelled" : "Your free trial has expired";
  const plans = ["starter", "professional", "business", "enterprise", "enterprise_plus"];
  return /* @__PURE__ */ jsxs("div", { style: { maxWidth: 1100, margin: "0 auto", padding: "32px 16px", fontFamily }, children: [
    /* @__PURE__ */ jsxs("div", { style: { textAlign: "center", marginBottom: 32 }, children: [
      /* @__PURE__ */ jsx("div", { style: { fontSize: 48 }, children: "\u26A0\uFE0F" }),
      /* @__PURE__ */ jsx("h2", { style: { margin: "8px 0", fontSize: 28 }, children: statusMessage }),
      /* @__PURE__ */ jsx("p", { style: { color: "#666", fontSize: 16 }, children: "Choose a plan to continue using TurnKeyOS" })
    ] }),
    /* @__PURE__ */ jsx("div", { style: { display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }, children: plans.map((tier) => {
      const limits = PLAN_LIMITS[tier];
      const isPopular = tier === "professional";
      return /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            border: isPopular ? "2px solid #1976d2" : "1px solid #ddd",
            borderRadius: 8,
            padding: 24,
            width: 180,
            textAlign: "center",
            position: "relative",
            backgroundColor: "#fff"
          },
          children: [
            isPopular && /* @__PURE__ */ jsx("span", { style: {
              position: "absolute",
              top: -12,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#1976d2",
              color: "#fff",
              padding: "2px 10px",
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 600
            }, children: "Most Popular" }),
            /* @__PURE__ */ jsx("h3", { style: { margin: "8px 0", fontSize: 16 }, children: PLAN_DISPLAY_NAMES[tier] }),
            /* @__PURE__ */ jsxs("div", { style: { fontSize: 28, fontWeight: 700, color: "#1976d2", margin: "8px 0" }, children: [
              "$",
              limits.priceMonthly,
              /* @__PURE__ */ jsx("span", { style: { fontSize: 13, color: "#666", fontWeight: 400 }, children: "/mo" })
            ] }),
            /* @__PURE__ */ jsxs("p", { style: { fontSize: 13, color: "#666", margin: "4px 0" }, children: [
              limits.maxUsers,
              " users"
            ] }),
            /* @__PURE__ */ jsxs("p", { style: { fontSize: 13, color: "#666", margin: "4px 0 16px" }, children: [
              limits.maxCompanies === -1 ? "Unlimited" : limits.maxCompanies,
              " companies"
            ] }),
            /* @__PURE__ */ jsx(
              "a",
              {
                href: `/billing?plan=${tier}`,
                style: {
                  display: "inline-block",
                  padding: "8px 20px",
                  borderRadius: 4,
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: 600,
                  ...isPopular ? { backgroundColor: "#1976d2", color: "#fff", border: "none" } : { backgroundColor: "#fff", color: "#1976d2", border: "1px solid #1976d2" }
                },
                children: "Choose Plan"
              }
            )
          ]
        },
        tier
      );
    }) })
  ] });
}
var initialState2 = {
  currentSessionId: null,
  currentPage: 1,
  responses: {},
  participantInfo: null,
  assessmentType: null,
  isLoading: false,
  questionnaireComplete: false
};
function assessmentReducer(state, action) {
  switch (action.type) {
    case "SET_SESSION_ID":
      return { ...state, currentSessionId: action.payload };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPage: action.payload };
    case "UPDATE_RESPONSE":
      return {
        ...state,
        responses: {
          ...state.responses,
          [action.payload.questionId]: action.payload.answer
        }
      };
    case "SET_RESPONSES":
      return {
        ...state,
        responses: action.payload
      };
    case "SET_PARTICIPANT_INFO":
      return { ...state, participantInfo: action.payload };
    case "SET_ASSESSMENT_TYPE":
      return { ...state, assessmentType: action.payload };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "CLEAR_PAGE_RESPONSES":
      const filteredResponses = Object.keys(state.responses).reduce((acc, key) => {
        if (!key.includes(`_q${action.payload}_`)) {
          acc[key] = state.responses[key];
        }
        return acc;
      }, {});
      return { ...state, responses: filteredResponses };
    case "SET_QUESTIONNAIRE_COMPLETE":
      return { ...state, questionnaireComplete: action.payload };
    case "RESET_STATE":
      return initialState2;
    default:
      return state;
  }
}
var AssessmentContext = createContext(void 0);
function AssessmentProvider({ children }) {
  const [state, dispatch] = useReducer(assessmentReducer, initialState2);
  const setSessionId = useCallback((id) => {
    dispatch({ type: "SET_SESSION_ID", payload: id });
  }, []);
  const setCurrentPage = useCallback((page) => {
    dispatch({ type: "SET_CURRENT_PAGE", payload: page });
  }, []);
  const updateResponse = useCallback((questionId, answer) => {
    dispatch({ type: "UPDATE_RESPONSE", payload: { questionId, answer } });
  }, []);
  const setParticipantInfo = useCallback((info) => {
    dispatch({ type: "SET_PARTICIPANT_INFO", payload: info });
  }, []);
  const setLoading = useCallback((loading) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  }, []);
  const setQuestionnaireComplete = useCallback((complete) => {
    dispatch({ type: "SET_QUESTIONNAIRE_COMPLETE", payload: complete });
  }, []);
  const resetState = useCallback(() => {
    dispatch({ type: "RESET_STATE" });
  }, []);
  const getResponse = useCallback(
    (questionId) => {
      return state.responses[questionId];
    },
    [state.responses]
  );
  const getTotalResponses = useCallback(() => {
    return Object.keys(state.responses).length;
  }, [state.responses]);
  const value = {
    state,
    dispatch,
    setSessionId,
    setCurrentPage,
    updateResponse,
    setParticipantInfo,
    setLoading,
    setQuestionnaireComplete,
    resetState,
    getResponse,
    getTotalResponses
  };
  return /* @__PURE__ */ jsx(AssessmentContext.Provider, { value, children });
}
function useAssessment() {
  const context = useContext(AssessmentContext);
  if (context === void 0) {
    throw new Error("useAssessment must be used within an AssessmentProvider");
  }
  return context;
}
function NotFoundPage({ onGoHome }) {
  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = "/";
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "80vh",
        textAlign: "center",
        padding: "2rem",
        fontFamily: "Roboto, sans-serif"
      },
      children: [
        /* @__PURE__ */ jsx("div", { style: { marginBottom: "1.5rem" }, children: /* @__PURE__ */ jsxs("span", { style: { fontSize: "1.5rem", fontWeight: 700 }, children: [
          "TurnKeyOS",
          /* @__PURE__ */ jsx("span", { style: { color: "#3b82f6" }, children: ".AI" })
        ] }) }),
        /* @__PURE__ */ jsx(
          "h1",
          {
            style: {
              fontSize: "6rem",
              fontWeight: 700,
              color: "#3b82f6",
              margin: "0 0 0.5rem 0",
              lineHeight: 1
            },
            children: "404"
          }
        ),
        /* @__PURE__ */ jsx(
          "h2",
          {
            style: {
              fontSize: "1.5rem",
              fontWeight: 500,
              color: "#333",
              margin: "0 0 1rem 0"
            },
            children: "Page not found"
          }
        ),
        /* @__PURE__ */ jsx(
          "p",
          {
            style: {
              fontSize: "1rem",
              color: "#666",
              maxWidth: "400px",
              margin: "0 0 2rem 0"
            },
            children: "The page you're looking for doesn't exist or has been moved."
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            onClick: handleGoHome,
            style: {
              padding: "0.75rem 2rem",
              fontSize: "1rem",
              fontWeight: 500,
              color: "#fff",
              backgroundColor: "#3b82f6",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "background-color 0.2s"
            },
            onMouseOver: (e) => e.currentTarget.style.backgroundColor = "#2563eb",
            onMouseOut: (e) => e.currentTarget.style.backgroundColor = "#3b82f6",
            children: "Go back home"
          }
        )
      ]
    }
  );
}

export { ACTION_STATUS, ANIMATION_DURATIONS, APP_NAME, APP_VERSION, ASSESSMENT_TYPES, AssessmentProvider, AuthProvider, CHART_COLORS, COLLECTIONS, ConditionalRender, DATE_FORMATS, DEBOUNCE_DELAYS, DEFAULT_LOCALE, DEFAULT_PAGE_SIZE, ENTRY_TYPES, ERROR_CODES, INTERVAL_TYPES, LocaleProvider, NotFoundPage, PAGE_SIZE_OPTIONS, PLAN_DISPLAY_NAMES, PLAN_LIMITS, PLAN_TIERS, PRIORITIES, Paywall, ROLES, ROLE_HIERARCHY, RTL_LOCALES, RequireAuth, RequireMinRole, RequireOrg, RequireRole, SESSION_STATUS, STATUS_COLORS, STORAGE_KEYS, STORAGE_PATHS, SUBSCRIPTION_STATUSES, SUPPORTED_LOCALES, SubscriptionProvider, TRIAL_DURATION_DAYS, TrialBanner, app, appCheck, auth, batchWrite, callFunction, capitalize, clearSession, clearStorage, cloudFunctions, connectEmulators, createDocument, createNamespacedStorage, createValidator, db, deleteDocument, deleteFile as deleteStorageFile, displayNameSchema, emailSchema, executeTransaction, formatBytes, formatCurrency, formatDate, formatDateTime, formatNumber, formatPercent, formatPhone, formatRelativeTime, formatTime, functions, generateFilePath, getCollection, getDocRef, getDocument, getDocuments, getFileUrl, getSessionItem, getStorageItem, initializeFirebase, isUsingEmulators, isValidEmail, isValidPassword, isValidPhone, isValidUrl, listFiles, organizationNameSchema, passwordSchema, phoneSchema, removeSessionItem, removeStorageItem, requiredString, setDocument, setSessionItem, setStorageItem, storage, subscribeToCollection, subscribeToDocument, toTitleCase, truncate, updateDocument, uploadFile, uploadFileWithProgress, urlSchema, useAssessment, useAuth, useCloudFunction, useCloudFunctionQuery, useFirestoreCollection, useFirestoreDoc, useIsDesktop, useIsLargeDesktop, useIsMobile, useIsTablet, useLocale, useMediaQuery, useOrganization, useOrganizationById, useOrganizationId, usePrefersDarkMode, usePrefersReducedMotion, useRoles, useSubscription, useSubscriptionContext, useTranslation, useUser };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map